import React, { useState, useEffect, useRef } from 'react';
import { 
  // Shared & Demo Icons
  CheckCheck, Phone, Video, MoreVertical, Send, 
  Paperclip, Smile, Activity, ShieldAlert, 
  FileText, Clock, Calendar, LogOut, Users, Bell,
  ChevronLeft, Plus, Mic, Battery, Wifi, Signal, User,
  LayoutDashboard, MessageSquare, CreditCard, Settings,
  Menu, X, Search, ChevronRight, CheckCircle2, AlertTriangle,
  Image as ImageIcon, Upload, UserPlus,
  
  // Brochure Specific Icons
  Stethoscope, CalendarClock, HeartHandshake, 
  Save, Edit3, ArrowLeft, Shield, BarChart3, 
  ShieldCheck, Code, Rocket, Microscope, ChevronDown, 
  ListPlus, GitCommit, Trash2, Lock, Key
} from 'lucide-react';

// ============================================================================
// 1. GLOBAL CONSTANTS & DATA
// ============================================================================

const apiKey = "AIzaSyBtfl-3wVcNbsqt3w_bmZt_d28lrUY2BHw"; // Hardcode your actual Gemini API Key here (e.g., "AIzaSy...")

const SYSTEM_PROMPT = `
You are CuRA AI, an empathetic, medical-grade WhatsApp virtual assistant for a modern IVF clinic named 'Canvas Future Fertility'. 
You are speaking with a patient named Sarah.
Your tone must be warm, reassuring, medically accurate, and concise (WhatsApp style).
If the user asks general questions, you are open 8 AM - 8 PM in Bengaluru.
If the patient reports 'red flag' symptoms (severe bloating, shortness of breath, heavy bleeding, extreme pain), tell them this is a medical emergency, you are escalating to the on-call doctor, and they should visit the ER immediately.
Do not provide medical diagnoses.
Keep responses short.
`;

const initialBrochureData = {
  auth: {
    username: "admin",
    password: "admin123"
  },
  branding: {
    logoUrl: "https://canvasfuture.com/wp-content/uploads/2025/10/LOGO-_Main.png",
    brandName: "CuRA AI",
    fontFamily: "Lexend",
    fontType: "google",
    fontUrl: "https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700;800;900&display=swap",
    customFontUrl: ""
  },
  hero: {
    title: "CuRA AI",
    subtitle: "The Intelligent WhatsApp Automation Platform for Modern IVF Clinics",
    description: "By bridging the critical gap between clinical excellence and the patient experience, we transform your clinic's official WhatsApp number into a 24/7 digital care coordinator",
  },
  executiveSummary: [
    { id: "admin", title: "The Smart Front-Desk", icon: "CalendarClock", points: ["24/7 AI Booking & Rescheduling", "Automated Patient Registration", "Real-time Clinic FAQ", "Instant Delay Alert Broadcasts"] },
    { id: "clinical", title: "The Clinical Companion", icon: "Stethoscope", points: ["Smart Med Reminders + 'Confirm Dose'", "Automated 'How-to' Injection Videos", "Fail-safe Trigger Shot Alerts", "Secure Lab Report Delivery"] },
    { id: "care", title: "The AI Counselor", icon: "HeartHandshake", points: ["24/7 Med-Grade Protocol Q&A", "Two-Week Wait (TWW) Support", "Symptom Triage (OHSS Alerts)", "Multi-lingual Support"] }
  ],
  deepDive: [
    {
      id: "f1",
      title: "Smart Front-Desk",
      desc: "Acting as a digital twin of your best receptionist, working 24/7 to ensure no lead goes unanswered.",
      phoneType: "booking",
      details: {
        longDesc: "The Smart Front-Desk completely automates your clinic's top-of-funnel operations. By capturing high-intent patients immediately via WhatsApp, it eliminates phone line congestion and manual data entry, ensuring your staff only handles complex administrative tasks.",
        features: [
          { title: "Intelligent Triage & Routing", text: "Automatically categorizes leads based on intent and routes them to the correct department." },
          { title: "Seamless EMR Synchronization", text: "Patient intake forms filled securely on WhatsApp are pushed directly via APIs into your EMR." },
          { title: "Dynamic Schedule Protection", text: "Proactively messages waiting patients to adjust arrival times if surgeries are delayed." }
        ],
        benefits: ["Zero missed patient leads after hours", "60% reduction in front-desk call volume", "Instant, paperless patient onboarding"],
        technicalSpecs: ["HL7/FHIR Compliant Rest APIs", "99.9% Uptime SLA Guarantee", "Real-time bi-directional EMR Sync", "Azure Active Directory Auth"],
        useCases: ["First-time patient onboarding flow", "Automated Follicular Scan bookings", "Real-time delay & queue broadcasts"],
        clinicalWorkflow: [
          { step: "01", title: "Patient Initiation", desc: "Patient clicks a WhatsApp link from your Google My Business at 11:30 PM." },
          { step: "02", title: "AI Booking", desc: "AI answers FAQs and offers consultation slots pulled from your EMR." },
          { step: "03", title: "Digital Intake", desc: "A secure form is sent. Patient uploads past reports directly into chat." },
          { step: "04", title: "EMR Sync", desc: "Before clinic opens, doctor has the patient's complete file synced to their dashboard." }
        ],
        expandedBenefits: [
          { title: "Zero-Leakage Lead Capture", desc: "Engages every inquiry immediately, drastically increasing ad conversion rates." },
          { title: "Front-Desk Workload Reduction", desc: "Saves your reception team an average of 4-5 hours daily." },
          { title: "Automated Reputation Management", desc: "Eliminates waiting room frustration, correlating to higher Google Review scores." }
        ],
        faqs: [
          { q: "Does this replace our current reception staff?", a: "No. It handles the 80% of repetitive queries, allowing staff to handle complex scheduling." },
          { q: "How does it handle complex schedules?", a: "The AI reads your EMR calendar rules and strictly enforces booking parameters." },
          { q: "Is patient data secure?", a: "Yes. All data passes through Meta's encrypted API directly into your Azure EMR instance." }
        ]
      }
    },
    {
      id: "f2",
      title: "Clinical Companion",
      desc: "In IVF, timing is everything. Protect the cycle with closed-loop medication tracking and trigger shot fail-safes.",
      phoneType: "meds",
      details: {
        longDesc: "A single missed dose or incorrectly timed hCG trigger can compromise an entire IVF cycle. The Clinical Companion acts as an automated safeguard against human error, enforcing strict adherence to your protocols.",
        features: [
          { title: "Closed-Loop 'Confirm Dose'", text: "Requires active confirmation. If unconfirmed within 15 minutes, it escalates to the coordinator." },
          { title: "Contextual Video Care", text: "Delivers step-by-step injection videos 10 minutes before the scheduled dose." },
          { title: "Trigger Shot Fail-Safe", text: "High-priority, multi-channel escalation logic specifically for trigger injections." }
        ],
        benefits: ["Eliminates cancelled cycles due to non-compliance", "Reduces patient anxiety regarding self-injections", "Provides comprehensive audit trail"],
        technicalSpecs: ["End-to-End Meta Encryption", "SMS/Voice call fallbacks", "Automated escalating alert logic", "Immutable dosage logging"],
        useCases: ["FSH / Cetrotide reminders", "Crucial hCG Trigger Shot escalation", "Fasting & Procedure prep"],
        clinicalWorkflow: [
          { step: "01", title: "Protocol Prescription", desc: "Physician enters stimulation protocol into the EMR." },
          { step: "02", title: "Schedule Gen", desc: "AI translates this into a patient-friendly WhatsApp schedule." },
          { step: "03", title: "Interactive Alerts", desc: "Patient receives 'How-To' video followed by 'Confirm Dose' prompt." },
          { step: "04", title: "Escalation", desc: "If unconfirmed, an automated alert pings the on-duty IVF coordinator." }
        ],
        expandedBenefits: [
          { title: "Revenue & Cycle Protection", desc: "Acts as an insurance policy for your clinical outcomes and revenue." },
          { title: "Comprehensive Audit Trail", desc: "Every reminder is time-stamped and logged in the EMR." },
          { title: "Reduced Anxiety", desc: "Delivering videos at the moment of injection reduces panic calls by up to 70%." }
        ],
        faqs: [
          { q: "What if there is no internet?", a: "We implement SMS and voice-call fallback mechanisms for critical events." },
          { q: "Can we use our own videos?", a: "Yes, we can map your specific video links to corresponding alerts." },
          { q: "Does it handle mid-cycle dose adjustments?", a: "Yes. Any adjustment in your EMR instantly updates the AI's schedule." }
        ]
      }
    },
    {
      id: "f3",
      title: "AI Counselor",
      desc: "Provide 24/7 empathetic support during high-anxiety periods like the Two-Week Wait, with real-time OHSS symptom triage.",
      phoneType: "counselor",
      details: {
        longDesc: "The emotional toll of fertility treatments often peaks outside clinic hours. The AI Counselor is trained on your specific medical protocols to provide safe, instant, and empathetic responses when patients feel most vulnerable.",
        features: [
          { title: "Automated Symptom Triage (OHSS)", text: "Continuously scans patient messages for clinical 'Red-Flag' keywords (e.g., severe bloating, shortness of breath) and instantly triggers an emergency alert to the on-call doctor." },
          { title: "The Two-Week Wait (TWW) Module", text: "A specialized daily engagement sequence designed to provide psychological reassurance, mental health check-ins, and vital protocol reminders during the anxious post-transfer period." },
          { title: "Protocol-Specific NLP Engine", text: "Instead of generic answers, the AI is fine-tuned on your clinic's specific documentation. If a patient asks 'Can I travel after transfer?', the AI replies exactly as your Lead IVF Specialist would." }
        ],
        benefits: ["Prevents clinical emergencies through early symptom detection", "Significantly improves patient satisfaction and online reviews", "Differentiates your clinic by offering continuous, empathetic care"],
        technicalSpecs: ["Azure Medical-Grade NLP Models", "Custom LLM Fine-Tuning Environment", "Zero-PHI retention configurations", "Native multi-lingual processing"],
        useCases: ["TWW anxiety management", "Post-op OHSS symptom monitoring", "24/7 Clinic Policy Q&A"],
        clinicalWorkflow: [
          { step: "01", title: "Post-Transfer Initialization", desc: "Upon embryo transfer completion in the EMR, the TWW Engagement sequence automatically begins." },
          { step: "02", title: "Daily Reassurance & Monitoring", desc: "The AI sends daily micro-updates and checks in on the patient's physical and mental state." },
          { step: "03", title: "Query Handling & Triage", desc: "The patient reports 'sharp abdominal pain'. The NLP engine detects this as a high-risk symptom based on your parameters." },
          { step: "04", title: "Emergency Routing", desc: "The AI immediately replies advising the patient to sit down, halts standard chat, and sends an urgent push notification to the duty doctor." }
        ],
        expandedBenefits: [
          { title: "Clinical Liability Mitigation", desc: "By instantly triaging high-risk symptoms (like OHSS or ectopic pregnancy indicators) 24/7, the clinic significantly reduces the risk of delayed emergency interventions." },
          { title: "Standardization of Medical Advice", desc: "Ensure that every patient receives the exact same, doctor-approved advice regarding diet, travel, and rest, eliminating variance." },
          { title: "Patient Retention & Loyalty", desc: "Clinics that support patients emotionally during the TWW see vastly higher return rates for subsequent cycles and generate the highest volume of referrals." }
        ],
        faqs: [
          { q: "Is there a risk of the AI giving incorrect medical advice (hallucinating)?", a: "We utilize a 'RAG' (Retrieval-Augmented Generation) architecture. The AI can ONLY pull answers from the verified medical protocols you provide. If a query falls outside that documentation, it routes to a human." },
          { q: "How are emergency alerts delivered to doctors at 2 AM?", a: "Emergency escalations bypass WhatsApp and trigger a high-priority automated phone call and SMS to the designated on-call physician's mobile device." },
          { q: "Can the AI understand regional languages?", a: "Yes. The underlying Azure AI natively understands and translates queries in Hindi, Kannada, Tamil, Telugu, and others." }
        ]
      }
    }
  ],
  journey: {
    title: "The End-to-End Patient Journey",
    subtitle: "Mapping every crucial touchpoint where intelligent automation elevates patient care and clinic efficiency.",
    stages: [
      { id: "s1", name: "Pre-Consultation & Intake", icon: "User", brief: "Lead capture, FAQs, and digital EMR onboarding." },
      { id: "s2", name: "Stimulation & Tracking", icon: "Activity", brief: "Medication compliance, scan scheduling, and video guidance." },
      { id: "s3", name: "Retrieval & Fertilization", icon: "Microscope", brief: "Trigger fail-safes, prep protocols, and lab updates." },
      { id: "s4", name: "Transfer & Two-Week Wait", icon: "HeartHandshake", brief: "TWW empathy support, OHSS triage, and result delivery." }
    ],
    detailedImpact: [
      {
        stageName: "Stage 1: Pre-Consultation & Digital Intake",
        doctorInteraction: "Initial assessment, reviewing medical history and previous scan reports.",
        traditionalBottleneck: "Patients spend 30 minutes in the waiting room filling out 5-page paper forms. Front desk is tied up answering basic pricing queries on the phone.",
        aiImpact: "CuRA AI automatically answers FAQs via WhatsApp, books the slot dynamically, and sends a digital intake form. The patient uploads previous semen analysis and ultrasound reports from home.",
        metricData: "Saves 15 minutes of administrative overhead per new patient and increases after-hours lead conversion by 42%."
      },
      {
        stageName: "Stage 2: Ovarian Stimulation & Monitoring",
        doctorInteraction: "Follicular tracking scans and adjusting daily hormone dosages based on growth.",
        traditionalBottleneck: "Patients misinterpret verbal dosage changes or forget to administer FSH injections at the exact required time, leading to poor follicle response.",
        aiImpact: "Direct synchronization with the EMR generates a personalized WhatsApp timeline. Patients receive a 10-minute warning with a 'How-To' video, followed by a mandatory 'Confirm Dose' button.",
        metricData: "Reduces medication administration errors by 94% and cuts inbound nursing questions by over 60%."
      },
      {
        stageName: "Stage 3: Egg Retrieval & Lab Fertilization",
        doctorInteraction: "Performing the OPU, updating patients on egg yield, and subsequent embryo grading.",
        traditionalBottleneck: "A missed midnight hCG Trigger Shot completely ruins a cycle. Post-retrieval, patients repeatedly call the lab for fertilization updates, disrupting embryologists.",
        aiImpact: "Deploy high-priority, escalating alerts for Trigger Shots with a nurse-override protocol. Post-retrieval, the AI pushes secure, automated day-3 and day-5 embryo development updates directly from the EMR.",
        metricData: "Achieves 100% trigger-shot compliance via escalation pathways. Shields embryologists from 85% of routine update calls."
      },
      {
        stageName: "Stage 4: Embryo Transfer & Two-Week Wait",
        doctorInteraction: "Post-transfer instructions and managing patient anxiety/complications before the Beta hCG test.",
        traditionalBottleneck: "The TWW is highly stressful. Patients frantically search Google for symptoms. Late-night OHSS symptoms are sometimes missed until they become emergencies.",
        aiImpact: "Deploys a 14-day empathetic drip campaign with daily reassurance. The NLP engine actively monitors patient chats for 'Red-Flag' OHSS keywords, instantly alerting the on-call doctor if detected.",
        metricData: "Reduces TWW panic calls by 70%. Increases long-term patient retention and 5-star clinic reviews due to perceived 24/7 care."
      }
    ]
  },
  roadmap: [
    { phase: "Clinical Discovery", duration: "Week 1", desc: "Audit current communication bottlenecks and map standard protocols.", icon: "Search" },
    { phase: "Meta Verification", duration: "Week 1-2", desc: "Secure official Green Tick verification and submit templates.", icon: "ShieldCheck" },
    { phase: "System Integration", duration: "Week 3-4", desc: "Connect AI to EMR via APIs and configure routing logic.", icon: "Code" },
    { phase: "Go-Live & Training", duration: "Week 5+", desc: "Launch platform, train coordinators, and continuous optimization.", icon: "Rocket" }
  ],
  tiers: [
    { 
      name: "Lite", 
      target: "Boutique Clinics", 
      price: "Basic Automation", 
      setupFee: "135,000",
      monthlyFee: "25,000",
      features: ["15 Verified Templates", "Standard 24h Alerts", "Exportable CSV Intake", "Basic Keyword Auto-Replies"] 
    },
    { 
      name: "Basic", 
      target: "Mid-Sized Centers", 
      price: "Workflow Optimizer", 
      setupFee: "1,75,000",
      monthlyFee: "45,000",
      features: ["50 Verified Templates", "Standard IVF/IUI Flows", "Standard API to EMR", "Advanced Routing"] 
    },
    { 
      name: "Advanced", 
      target: "Enterprise Networks", 
      price: "Full AI Enterprise", 
      setupFee: "3,75,000",
      monthlyFee: "75,000",
      features: ["Unlimited Templates", "Confirm Dose Tracking", "Native Bi-Directional EMR", "Med-Grade Q&A & TWW"] 
    }
  ]
};

// ============================================================================
// 2. SHARED COMPONENTS (Brochure)
// ============================================================================

const GlassCard = ({ children, className = "", onClick }) => (
  <div onClick={onClick} className={`bg-white/10 backdrop-blur-lg border border-white/20 shadow-[0_8px_32px_0_rgba(31,38,135,0.37)] rounded-2xl ${className}`}>
    {children}
  </div>
);

const IconResolver = ({ name, className }) => {
  const icons = { Stethoscope, CalendarClock, HeartHandshake, Settings, Search, ShieldCheck, Code, Rocket, User, Activity, Microscope };
  const IconComponent = icons[name] || Settings;
  return <IconComponent className={className} />;
};

const FAQItem = ({ q, a }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-white/10 last:border-0">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center py-4 text-left text-white hover:text-teal-300 transition-colors focus:outline-none">
        <span className="font-semibold text-lg pr-8">{q}</span>
        <ChevronDown className={`shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-teal-400' : 'text-slate-400'}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-6 opacity-100' : 'max-h-0 opacity-0'}`}>
        <p className="text-slate-300 leading-relaxed pl-2 border-l-2 border-teal-500/50">{a}</p>
      </div>
    </div>
  );
};

const ROICalculator = () => {
  const [cycles, setCycles] = useState(50);
  const [revenue, setRevenue] = useState(150000);
  const [cancelRate, setCancelRate] = useState(3);
  
  const revProtected = Math.round((cycles * (cancelRate/100) * revenue) - (cycles * 0.005 * revenue));
  const staffSavings = Math.round(8 * 0.6 * 500 * 22);
  const noShowRecovery = Math.round(cycles * 0.15 * 0.5 * revenue);
  const monthlyImpact = revProtected + staffSavings + noShowRecovery;
  const annualImpact = monthlyImpact * 12;

  const formatINR = (num) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(num);

  return (
    <GlassCard className="p-6 md:p-8 mt-12 text-slate-800">
      <h3 className="text-2xl font-bold text-white mb-2">IVF Efficiency & ROI Simulator</h3>
      <p className="text-white/80 mb-8">Calculate your clinic's potential revenue protection and staff savings.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-6 bg-white/5 p-6 rounded-xl border border-white/10 text-white">
          <div>
            <div className="flex justify-between mb-2"><label className="text-sm font-medium text-white/90">Monthly IVF Cycles</label><span className="font-bold">{cycles}</span></div>
            <input type="range" min="10" max="300" value={cycles} onChange={(e)=>setCycles(e.target.value)} className="w-full accent-teal-400" />
          </div>
          <div>
            <div className="flex justify-between mb-2"><label className="text-sm font-medium text-white/90">Avg. Revenue per Cycle (INR)</label><span className="font-bold">{formatINR(revenue)}</span></div>
            <input type="range" min="50000" max="300000" step="10000" value={revenue} onChange={(e)=>setRevenue(e.target.value)} className="w-full accent-teal-400" />
          </div>
          <div>
            <div className="flex justify-between mb-2"><label className="text-sm font-medium text-white/90">Error/Cancellation Rate (%)</label><span className="font-bold">{cancelRate}%</span></div>
            <input type="range" min="0" max="10" value={cancelRate} onChange={(e)=>setCancelRate(e.target.value)} className="w-full accent-teal-400" />
          </div>
        </div>

        <div className="flex flex-col justify-center bg-gradient-to-br from-teal-500/20 to-blue-500/20 p-6 rounded-xl border border-teal-500/30 text-white">
          <div className="mb-6">
            <p className="text-sm text-teal-200 uppercase tracking-wider font-semibold">Total Projected Annual Impact</p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-blue-200 mt-2 drop-shadow-sm">{formatINR(annualImpact)}</h2>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center border-b border-white/10 pb-2">
              <span className="text-sm text-white/80">Revenue Protected</span><span className="font-semibold text-teal-200">{formatINR(revProtected * 12)}/yr</span>
            </div>
            <div className="flex justify-between items-center border-b border-white/10 pb-2">
              <span className="text-sm text-white/80">Recovered No-Shows</span><span className="font-semibold text-teal-200">{formatINR(noShowRecovery * 12)}/yr</span>
            </div>
            <div className="flex justify-between items-center pb-2">
              <span className="text-sm text-white/80">Overhead Savings</span><span className="font-semibold text-teal-200">{formatINR(staffSavings * 12)}/yr</span>
            </div>
          </div>
        </div>
      </div>
    </GlassCard>
  );
};

const BrochureWhatsAppMockup = ({ type, brandName = "CuRA AI" }) => {
  const getFlow = () => {
    switch(type) {
      case 'booking':
        return [
          { sender: 'user', text: "Hi, I need to book a consultation with Dr. Sharma." },
          { sender: 'bot', text: `Hello! Welcome to ${brandName}. Dr. Sharma has openings tomorrow. Please select a time:`, buttons: ["10:00 AM", "02:30 PM", "View Full Schedule"] },
          { sender: 'user', text: "10:00 AM" },
          { sender: 'bot', text: "Great. Please complete your digital intake form before your visit.", link: "Secure Form Link" }
        ];
      case 'meds':
        return [
          { sender: 'bot', text: "🚨 IMPORTANT REMINDER 🚨\n\nYour Gonal-F 300 IU injection is due in 15 minutes (08:00 PM)." },
          { sender: 'bot', text: "Need help? Watch this quick 30-sec instructional video.", link: "Play Video" },
          { sender: 'bot', text: "Please confirm once you have taken the dose.", buttons: ["✅ Dose Taken", "📞 Contact Nurse"] },
          { sender: 'user', text: "✅ Dose Taken" },
          { sender: 'bot', text: "Confirmed. Your protocol is on track! 🌟" }
        ];
      case 'counselor':
      default:
        return [
          { sender: 'user', text: "I'm 3 days post-transfer and having some cramping. Is this normal?" },
          { sender: 'bot', text: "Mild cramping can be normal during the Two-Week Wait. Let's make sure you're safe." },
          { sender: 'bot', text: "Are you experiencing severe bloating, shortness of breath, or heavy bleeding?", buttons: ["Yes, I have these", "No, just mild cramps"] },
          { sender: 'user', text: "No, just mild cramps" },
          { sender: 'bot', text: "That is reassuring. Please rest. If pain becomes severe, tap below.", buttons: ["🚨 Alert On-Call Doctor"] }
        ];
    }
  };

  const flow = getFlow();

  return (
    <div className="w-[280px] h-[550px] bg-slate-900 rounded-[2.5rem] p-3 shadow-2xl relative border-4 border-slate-700 flex flex-col mx-auto shrink-0">
      <div className="absolute top-0 inset-x-0 h-6 bg-slate-700 rounded-b-3xl w-40 mx-auto z-20"></div>
      <div className="bg-[#EFEAE2] flex-1 rounded-[2rem] overflow-hidden flex flex-col relative">
        <div className="bg-[#005C4B] text-white px-4 py-3 flex items-center gap-3 pt-6 shrink-0">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <Activity size={16} />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-sm flex items-center gap-1 truncate w-32">
              {brandName} <CheckCircle2 size={12} className="text-green-400 shrink-0" />
            </h4>
            <p className="text-[10px] text-white/70">Official Business Account</p>
          </div>
        </div>
        <div className="flex-1 p-3 overflow-y-auto flex flex-col gap-3 text-sm bg-[url('https://i.pinimg.com/originals/8c/98/99/8c98994518b575bfd8c949e91d20548b.jpg')] bg-cover bg-center">
          {flow.map((msg, idx) => (
            <div key={idx} className={`flex flex-col max-w-[85%] ${msg.sender === 'user' ? 'self-end' : 'self-start'}`}>
              <div className={`p-2.5 rounded-lg shadow-sm whitespace-pre-wrap ${msg.sender === 'user' ? 'bg-[#D9FDD3] rounded-tr-none' : 'bg-white rounded-tl-none'}`}>
                <p className="text-slate-800 leading-snug text-[13px]">{msg.text}</p>
                {msg.link && (
                  <div className="mt-2 text-blue-500 font-medium flex items-center gap-1 text-xs bg-blue-50 p-2 rounded cursor-pointer">
                    <ImageIcon size={14} /> {msg.link}
                  </div>
                )}
              </div>
              {msg.buttons && (
                <div className="flex flex-col gap-1 mt-1">
                  {msg.buttons.map((btn, bIdx) => (
                    <button key={bIdx} className="bg-white text-[#00A884] font-medium py-1.5 px-3 rounded-lg shadow-sm text-[13px] border border-slate-100 hover:bg-slate-50 transition-colors">
                      {btn}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="bg-[#f0f2f5] p-2 flex gap-2 items-center shrink-0">
          <div className="bg-white rounded-full px-4 py-2 flex-1 text-slate-400 text-sm">Message...</div>
          <div className="bg-[#00A884] w-10 h-10 rounded-full flex items-center justify-center text-white">
            <MessageSquare size={18} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// 3. BROCHURE APPLICATION SUB-VIEWS
// ============================================================================

const FeatureDetailView = ({ feature, onBack, brandName }) => {
  if (!feature) return null;
  const fDetails = feature.details;

  return (
    <div className="relative z-10 max-w-7xl mx-auto px-6 pb-32 pt-12 animate-in fade-in slide-in-from-bottom-8 duration-500">
      <button onClick={onBack} className="flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors mb-10 font-bold bg-white/5 hover:bg-white/10 px-4 py-2 rounded-xl border border-white/10 w-fit">
        <ArrowLeft size={20} /> Back to Full Brochure
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-7 xl:col-span-8 space-y-16">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/20 border border-teal-500/30 text-teal-300 text-sm font-semibold mb-6">Detailed Capability Module</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-teal-100 mb-6 leading-tight">{feature.title}</h1>
            <p className="text-xl text-slate-300 leading-relaxed font-light">{fDetails.longDesc}</p>
          </div>

          <div className="space-y-8">
            <h3 className="text-3xl font-bold text-white flex items-center gap-3"><Activity className="text-teal-400" size={32} /> Clinical Workflow & Usage</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
              <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-teal-500/0 via-teal-500/20 to-teal-500/0 -translate-y-1/2 z-0"></div>
              {fDetails.clinicalWorkflow.map((flow, idx) => (
                <GlassCard key={idx} className="p-6 relative z-10 bg-slate-900/60 border-teal-500/20">
                  <div className="text-4xl font-black text-white/5 mb-4 absolute top-2 right-4">{flow.step}</div>
                  <h4 className="text-lg font-bold text-teal-300 mb-2 relative z-10">{flow.title}</h4>
                  <p className="text-slate-300 text-sm leading-relaxed relative z-10">{flow.desc}</p>
                </GlassCard>
              ))}
            </div>
          </div>

          <div className="space-y-8 bg-gradient-to-br from-teal-900/40 to-blue-900/40 border border-teal-500/30 rounded-3xl p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -top-10 -right-10 opacity-5 pointer-events-none"><Shield size={250} /></div>
            <h3 className="text-3xl font-bold text-white mb-2 relative z-10 flex items-center gap-3"><BarChart3 className="text-teal-400" size={32} /> In-Depth Clinical & ROI Impact</h3>
            <div className="space-y-8 relative z-10">
              {fDetails.expandedBenefits.map((benefit, idx) => (
                <div key={idx} className="flex gap-4 items-start">
                  <div className="mt-1 bg-teal-500/20 rounded-full p-2 shrink-0 shadow-[0_0_15px_rgba(45,212,191,0.2)]"><CheckCircle2 size={24} className="text-teal-400" /></div>
                  <div><h4 className="text-xl font-bold text-white mb-2">{benefit.title}</h4><p className="text-slate-300 leading-relaxed">{benefit.desc}</p></div>
                </div>
              ))}
            </div>
          </div>

          {fDetails.faqs.length > 0 && (
            <div className="space-y-6 pt-8 border-t border-white/10">
              <h3 className="text-3xl font-bold text-white flex items-center gap-3 mb-8"><MessageSquare className="text-teal-400" size={32} /> Frequently Asked Questions</h3>
              <div className="bg-slate-900/50 rounded-2xl p-6 border border-white/10">
                {fDetails.faqs.map((faq, idx) => <FAQItem key={idx} q={faq.q} a={faq.a} />)}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-8 h-fit flex justify-center pt-2 pb-10">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-b from-teal-500/20 to-transparent blur-3xl -z-10 rounded-full transform scale-110"></div>
            <BrochureWhatsAppMockup type={feature.phoneType || 'counselor'} brandName={brandName} />
            <div className="absolute -bottom-6 -left-6 bg-slate-800/90 backdrop-blur-md border border-white/10 p-4 rounded-2xl shadow-xl flex items-center gap-3">
              <ShieldCheck className="text-green-400" size={24} />
              <div><p className="text-white text-xs font-bold leading-tight">DPDP 2023</p><p className="text-slate-400 text-[10px]">Compliant System</p></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const JourneyDetailView = ({ journey, onBack }) => (
  <div className="relative z-10 max-w-7xl mx-auto px-6 pb-32 pt-12 animate-in fade-in slide-in-from-bottom-8 duration-500">
    <button onClick={onBack} className="flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors mb-10 font-bold bg-white/5 hover:bg-white/10 px-4 py-2 rounded-xl border border-white/10 w-fit">
      <ArrowLeft size={20} /> Back to Full Brochure
    </button>

    <div className="text-center mb-16 max-w-3xl mx-auto">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-300 text-sm font-semibold mb-6">Patient Journey Matrix</div>
      <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-blue-100 mb-6 leading-tight">Interaction & Impact Mapping</h1>
      <p className="text-xl text-slate-300 leading-relaxed font-light">A comprehensive breakdown of how intelligent automation intercepts traditional IVF bottlenecks at every phase of treatment.</p>
    </div>

    <div className="space-y-12">
      {journey.detailedImpact.map((stage, idx) => (
        <GlassCard key={idx} className="overflow-hidden border border-white/10">
          <div className="bg-slate-800/80 px-8 py-5 border-b border-white/10 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold">{idx + 1}</div>
            <h3 className="text-2xl font-bold text-white">{stage.stageName}</h3>
          </div>
          
          <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2"><Stethoscope size={16}/> Clinical Context</h4>
              <p className="text-slate-200 text-lg">{stage.doctorInteraction}</p>
            </div>
            <div className="space-y-4 border-l-0 lg:border-l border-white/10 lg:pl-8">
              <h4 className="text-sm font-semibold text-rose-400 uppercase tracking-wider flex items-center gap-2"><ShieldAlert size={16}/> Traditional Bottleneck</h4>
              <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4">
                <p className="text-rose-200/90">{stage.traditionalBottleneck}</p>
              </div>
            </div>
            <div className="space-y-4 border-l-0 lg:border-l border-white/10 lg:pl-8">
              <h4 className="text-sm font-semibold text-teal-400 uppercase tracking-wider flex items-center gap-2"><Rocket size={16}/> Automated Impact</h4>
              <p className="text-slate-200">{stage.aiImpact}</p>
              <div className="mt-4 pt-4 border-t border-teal-500/20">
                <span className="block text-xs text-teal-500 font-bold uppercase mb-1">Measurable Result</span>
                <span className="text-teal-300 font-semibold text-lg leading-tight">{stage.metricData}</span>
              </div>
            </div>
          </div>
        </GlassCard>
      ))}
    </div>
  </div>
);

const FullPageCMS = ({ content, setContent, onClose }) => {
  const [activeTab, setActiveTab] = useState('general');
  const tabs = [
    { id: 'general', label: 'General & Hero' },
    { id: 'exec', label: 'Executive Summary' },
    { id: 'modules', label: 'Feature Modules' },
    { id: 'journey', label: 'Patient Journey' },
    { id: 'roadmap', label: 'Implementation Roadmap' },
    { id: 'tiers', label: 'Pricing Tiers' },
    { id: 'security', label: 'Security & Access' }
  ];

  const updateContent = (section, field, value) => setContent(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  const updateArray = (section, index, field, value) => setContent(prev => { const newArr = [...prev[section]]; newArr[index] = { ...newArr[index], [field]: value }; return { ...prev, [section]: newArr }; });

  const StringArrayEditor = ({ label, value, onChange }) => (
    <div className="mb-4">
      <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">{label} (One per line)</label>
      <textarea value={Array.isArray(value) ? value.join('\n') : ''} onChange={(e) => onChange(e.target.value.split('\n'))} className="w-full bg-slate-900 border border-white/10 rounded-lg p-3 text-sm text-slate-200 h-32 focus:border-teal-500 focus:outline-none" />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-slate-900 flex flex-col overflow-hidden animate-in fade-in duration-300">
      <div className="h-16 border-b border-white/10 bg-slate-800/50 flex items-center justify-between px-6 shrink-0">
        <div className="flex items-center gap-3"><Settings className="text-teal-400" /><h2 className="text-xl font-bold text-white">Brochure Content Manager</h2></div>
        <button onClick={onClose} className="flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white px-6 py-2 rounded-lg font-bold transition-colors"><Save size={18} /> Save & Exit Editor</button>
      </div>
      <div className="flex-1 flex overflow-hidden">
        <div className="w-64 bg-slate-800/30 border-r border-white/10 flex flex-col p-4 gap-2 overflow-y-auto">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`text-left px-4 py-3 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}>
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-8 bg-slate-900">
          <div className="max-w-4xl mx-auto space-y-8 pb-20">
            {activeTab === 'general' && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-white mb-6 border-b border-white/10 pb-4">Branding & Hero Section</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div><label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Brand Name</label><input type="text" value={content.branding.brandName} onChange={(e) => updateContent('branding', 'brandName', e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-lg p-3 text-white focus:border-teal-500 focus:outline-none" /></div>
                  <div><label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Logo URL</label><input type="text" value={content.branding.logoUrl} onChange={(e) => updateContent('branding', 'logoUrl', e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-lg p-3 text-white focus:border-teal-500 focus:outline-none" /></div>
                </div>
                <div className="p-4 bg-slate-900/50 rounded-xl border border-white/5 space-y-4 mb-6">
                  <h4 className="font-bold text-white border-b border-white/10 pb-2">Typography Settings</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Font Source</label>
                      <select value={content.branding.fontType || 'google'} onChange={(e) => updateContent('branding', 'fontType', e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-lg p-3 text-white focus:border-teal-500 focus:outline-none mb-4">
                        <option value="google">Google Fonts (URL)</option>
                        <option value="custom">Custom Font File (.woff, .ttf)</option>
                      </select>
                      <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Font Family Name</label>
                      <input type="text" value={content.branding.fontFamily} onChange={(e) => updateContent('branding', 'fontFamily', e.target.value)} placeholder="e.g., Roboto" className="w-full bg-slate-800 border border-white/10 rounded-lg p-3 text-white focus:border-teal-500 focus:outline-none" />
                    </div>
                    <div>
                      {(!content.branding.fontType || content.branding.fontType === 'google') ? (
                        <><label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Google Fonts Stylesheet URL</label><input type="text" value={content.branding.fontUrl} onChange={(e) => updateContent('branding', 'fontUrl', e.target.value)} placeholder="https://fonts.googleapis.com/css2?family=..." className="w-full bg-slate-800 border border-white/10 rounded-lg p-3 text-white text-xs focus:border-teal-500 focus:outline-none" /></>
                      ) : (
                        <><label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Direct Font File URL (.woff, .ttf, .otf)</label><input type="text" value={content.branding.customFontUrl || ''} onChange={(e) => updateContent('branding', 'customFontUrl', e.target.value)} placeholder="https://example.com/fonts/myfont.woff2" className="w-full bg-slate-800 border border-white/10 rounded-lg p-3 text-white text-xs focus:border-teal-500 focus:outline-none" /></>
                      )}
                    </div>
                  </div>
                </div>
                <div><label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Hero Title</label><input type="text" value={content.hero.title} onChange={(e) => updateContent('hero', 'title', e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-lg p-3 text-white text-xl font-bold focus:border-teal-500 focus:outline-none" /></div>
                <div><label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Hero Subtitle</label><input type="text" value={content.hero.subtitle} onChange={(e) => updateContent('hero', 'subtitle', e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-lg p-3 text-teal-300 focus:border-teal-500 focus:outline-none" /></div>
                <div><label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Hero Description</label><textarea value={content.hero.description} onChange={(e) => updateContent('hero', 'description', e.target.value)} rows="4" className="w-full bg-slate-800 border border-white/10 rounded-lg p-3 text-slate-300 focus:border-teal-500 focus:outline-none" /></div>
              </div>
            )}

            {activeTab === 'exec' && (
              <div className="space-y-8">
                <h3 className="text-2xl font-bold text-white mb-6 border-b border-white/10 pb-4">Executive Summary Cards</h3>
                {content.executiveSummary.map((card, idx) => (
                  <div key={card.id} className="bg-slate-800/50 border border-white/10 rounded-xl p-6">
                    <div className="grid grid-cols-2 gap-6 mb-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Card Title</label>
                        <input type="text" value={card.title} onChange={(e) => updateArray('executiveSummary', idx, 'title', e.target.value)} className="w-full bg-slate-900 border border-white/10 rounded-lg p-3 text-white" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Icon Name</label>
                        <input type="text" value={card.icon} onChange={(e) => updateArray('executiveSummary', idx, 'icon', e.target.value)} className="w-full bg-slate-900 border border-white/10 rounded-lg p-3 text-white" />
                      </div>
                    </div>
                    <StringArrayEditor label="Bullet Points" value={card.points} onChange={(val) => updateArray('executiveSummary', idx, 'points', val)} />
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'modules' && (
              <div className="space-y-8">
                <div className="flex justify-between items-center border-b border-white/10 pb-4 mb-6">
                  <h3 className="text-2xl font-bold text-white">Deep Dive Feature Modules</h3>
                  <button 
                    onClick={() => {
                      const newMod = { id: `mod_${Date.now()}`, title: "New Module", desc: "Brief desc.", phoneType: "counselor", details: { longDesc: "Long description.", features: [], benefits: [], technicalSpecs: [], useCases: [], clinicalWorkflow: [], expandedBenefits: [], faqs: [] } };
                      setContent(prev => ({...prev, deepDive: [...prev.deepDive, newMod]}));
                    }}
                    className="flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-lg hover:bg-blue-500/30 transition-colors text-sm font-bold"
                  >
                    <ListPlus size={16} /> Add New Module
                  </button>
                </div>

                {content.deepDive.map((mod, mIdx) => (
                  <div key={mod.id} className="bg-slate-800/40 border border-white/10 rounded-2xl p-6 space-y-6">
                    <div className="flex justify-between items-start">
                      <h4 className="text-xl font-bold text-teal-400">Module {mIdx + 1}: {mod.title}</h4>
                      <button onClick={() => setContent(prev => ({...prev, deepDive: prev.deepDive.filter((_, i) => i !== mIdx)}))} className="text-rose-400 hover:text-rose-300 bg-rose-500/10 p-2 rounded-lg"><Trash2 size={16}/></button>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Main Title</label>
                        <input type="text" value={mod.title} onChange={(e) => {
                          const newDeepDive = [...content.deepDive]; newDeepDive[mIdx].title = e.target.value; setContent({...content, deepDive: newDeepDive});
                        }} className="w-full bg-slate-900 border border-white/10 rounded-lg p-3 text-white" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">WhatsApp Mockup Type</label>
                        <select value={mod.phoneType} onChange={(e) => {
                          const newDeepDive = [...content.deepDive]; newDeepDive[mIdx].phoneType = e.target.value; setContent({...content, deepDive: newDeepDive});
                        }} className="w-full bg-slate-900 border border-white/10 rounded-lg p-3 text-white">
                          <option value="booking">Booking</option>
                          <option value="meds">Medications</option>
                          <option value="counselor">Counselor</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Brief Intro Description</label>
                      <textarea value={mod.desc} onChange={(e) => {
                          const newDeepDive = [...content.deepDive]; newDeepDive[mIdx].desc = e.target.value; setContent({...content, deepDive: newDeepDive});
                        }} rows="2" className="w-full bg-slate-900 border border-white/10 rounded-lg p-3 text-white" />
                    </div>

                    <div className="p-4 bg-slate-900/50 rounded-xl border border-white/5 space-y-4">
                      <h5 className="font-bold text-white border-b border-white/10 pb-2">Inner Page Content (Deep Dive)</h5>
                      <div>
                        <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Detailed Long Description</label>
                        <textarea value={mod.details?.longDesc || ''} onChange={(e) => {
                          const newDeepDive = [...content.deepDive]; newDeepDive[mIdx].details.longDesc = e.target.value; setContent({...content, deepDive: newDeepDive});
                        }} rows="3" className="w-full bg-slate-800 border border-white/10 rounded-lg p-3 text-white" />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <StringArrayEditor label="Core Benefits" value={mod.details?.benefits || []} onChange={(val) => {
                          const newDeepDive = [...content.deepDive]; newDeepDive[mIdx].details.benefits = val; setContent({...content, deepDive: newDeepDive});
                        }} />
                        <StringArrayEditor label="Technical Specs" value={mod.details?.technicalSpecs || []} onChange={(val) => {
                          const newDeepDive = [...content.deepDive]; newDeepDive[mIdx].details.technicalSpecs = val; setContent({...content, deepDive: newDeepDive});
                        }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'journey' && (
              <div className="space-y-8">
                <h3 className="text-2xl font-bold text-white mb-6 border-b border-white/10 pb-4">Patient Journey Mapping</h3>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Journey Title</label>
                  <input type="text" value={content.journey.title} onChange={(e) => updateContent('journey', 'title', e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-lg p-3 text-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Journey Subtitle</label>
                  <textarea value={content.journey.subtitle} onChange={(e) => updateContent('journey', 'subtitle', e.target.value)} rows="2" className="w-full bg-slate-800 border border-white/10 rounded-lg p-3 text-white" />
                </div>

                <div className="space-y-6 mt-8">
                  <h4 className="text-xl font-bold text-white">Journey Stages & Impacts</h4>
                  {content.journey.detailedImpact.map((impact, iIdx) => (
                    <div key={iIdx} className="bg-slate-800/50 border border-white/10 rounded-xl p-6 space-y-4">
                      <h5 className="font-bold text-blue-400">{impact.stageName}</h5>
                      <div>
                        <label className="block text-xs font-semibold text-slate-400 uppercase mb-1">Doctor Interaction</label>
                        <textarea value={impact.doctorInteraction} onChange={(e) => {
                          const newImpacts = [...content.journey.detailedImpact]; newImpacts[iIdx].doctorInteraction = e.target.value; updateContent('journey', 'detailedImpact', newImpacts);
                        }} rows="2" className="w-full bg-slate-900 border border-white/10 rounded-lg p-3 text-sm text-white" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-rose-400 uppercase mb-1">Traditional Bottleneck</label>
                        <textarea value={impact.traditionalBottleneck} onChange={(e) => {
                          const newImpacts = [...content.journey.detailedImpact]; newImpacts[iIdx].traditionalBottleneck = e.target.value; updateContent('journey', 'detailedImpact', newImpacts);
                        }} rows="2" className="w-full bg-rose-900/20 border border-rose-500/20 rounded-lg p-3 text-sm text-white" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-teal-400 uppercase mb-1">AI Impact & Metric</label>
                        <textarea value={impact.aiImpact} onChange={(e) => {
                          const newImpacts = [...content.journey.detailedImpact]; newImpacts[iIdx].aiImpact = e.target.value; updateContent('journey', 'detailedImpact', newImpacts);
                        }} rows="2" className="w-full bg-teal-900/20 border border-teal-500/20 rounded-lg p-3 text-sm text-white mb-2" />
                        <input type="text" placeholder="Metric Data" value={impact.metricData} onChange={(e) => {
                          const newImpacts = [...content.journey.detailedImpact]; newImpacts[iIdx].metricData = e.target.value; updateContent('journey', 'detailedImpact', newImpacts);
                        }} className="w-full bg-slate-900 border border-teal-500/50 rounded-lg p-3 text-sm text-teal-300" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'roadmap' && (
              <div className="space-y-8">
                <div className="flex justify-between items-center border-b border-white/10 pb-4 mb-6">
                  <h3 className="text-2xl font-bold text-white">Implementation Roadmap</h3>
                  <button 
                    onClick={() => setContent(prev => ({...prev, roadmap: [...prev.roadmap, { phase: "New Phase", duration: "Week X", desc: "Description", icon: "Code" }]}))}
                    className="flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-lg hover:bg-blue-500/30 transition-colors text-sm font-bold"
                  >
                    <ListPlus size={16} /> Add Phase
                  </button>
                </div>
                {content.roadmap.map((stage, sIdx) => (
                  <div key={sIdx} className="bg-slate-800/50 border border-white/10 rounded-xl p-4 flex gap-4 items-start">
                    <div className="flex-1 space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="col-span-2">
                          <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Phase Name</label>
                          <input type="text" value={stage.phase} onChange={(e) => updateArray('roadmap', sIdx, 'phase', e.target.value)} className="w-full bg-slate-900 border border-white/10 rounded-lg p-3 text-white" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Duration</label>
                          <input type="text" value={stage.duration} onChange={(e) => updateArray('roadmap', sIdx, 'duration', e.target.value)} className="w-full bg-slate-900 border border-white/10 rounded-lg p-3 text-teal-400 font-bold" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Description</label>
                        <input type="text" value={stage.desc} onChange={(e) => updateArray('roadmap', sIdx, 'desc', e.target.value)} className="w-full bg-slate-900 border border-white/10 rounded-lg p-3 text-slate-300 text-sm" />
                      </div>
                    </div>
                    <button onClick={() => setContent(prev => ({...prev, roadmap: prev.roadmap.filter((_, i) => i !== sIdx)}))} className="mt-8 text-rose-400 p-2"><Trash2 size={20}/></button>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'tiers' && (
              <div className="space-y-8">
                <div className="flex justify-between items-center border-b border-white/10 pb-4 mb-6">
                  <h3 className="text-2xl font-bold text-white">Pricing & Architecture Tiers</h3>
                  <button 
                    onClick={() => setContent(prev => ({...prev, tiers: [...prev.tiers, { name: "New Tier", target: "Target", price: "Focus", setupFee: "0", monthlyFee: "0", features: ["Feature 1"] }]}))}
                    className="flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-lg hover:bg-blue-500/30 transition-colors text-sm font-bold"
                  >
                    <ListPlus size={16} /> Add Tier
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {content.tiers.map((tier, tIdx) => (
                    <div key={tIdx} className="bg-slate-800/50 border border-white/10 rounded-xl p-6 relative">
                      <button onClick={() => setContent(prev => ({...prev, tiers: prev.tiers.filter((_, i) => i !== tIdx)}))} className="absolute top-4 right-4 text-rose-400"><Trash2 size={16}/></button>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-semibold text-slate-400 uppercase mb-1">Tier Name</label>
                          <input type="text" value={tier.name} onChange={(e) => updateArray('tiers', tIdx, 'name', e.target.value)} className="w-full bg-slate-900 border border-white/10 rounded-lg p-2 text-white font-bold" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-semibold text-slate-400 uppercase mb-1">Target</label>
                            <input type="text" value={tier.target} onChange={(e) => updateArray('tiers', tIdx, 'target', e.target.value)} className="w-full bg-slate-900 border border-white/10 rounded-lg p-2 text-sm text-teal-300" />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-slate-400 uppercase mb-1">Focus Tag</label>
                            <input type="text" value={tier.price} onChange={(e) => updateArray('tiers', tIdx, 'price', e.target.value)} className="w-full bg-slate-900 border border-white/10 rounded-lg p-2 text-sm text-white" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-semibold text-slate-400 uppercase mb-1">Setup Fee (₹)</label>
                            <input type="text" value={tier.setupFee} onChange={(e) => updateArray('tiers', tIdx, 'setupFee', e.target.value)} className="w-full bg-slate-900 border border-white/10 rounded-lg p-2 text-sm text-teal-400 font-bold" />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-slate-400 uppercase mb-1">Monthly Fee (₹)</label>
                            <input type="text" value={tier.monthlyFee} onChange={(e) => updateArray('tiers', tIdx, 'monthlyFee', e.target.value)} className="w-full bg-slate-900 border border-white/10 rounded-lg p-2 text-sm text-teal-400 font-bold" />
                          </div>
                        </div>
                        <StringArrayEditor label="Tier Features" value={tier.features} onChange={(val) => updateArray('tiers', tIdx, 'features', val)} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="space-y-8">
                <div className="flex justify-between items-center border-b border-white/10 pb-4 mb-6">
                  <h3 className="text-2xl font-bold text-white">Security & Access</h3>
                </div>
                <div className="bg-slate-800/50 border border-white/10 rounded-xl p-6 space-y-6 max-w-lg">
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Admin Username</label>
                    <input type="text" value={content.auth.username} onChange={(e) => updateContent('auth', 'username', e.target.value)} className="w-full bg-slate-900 border border-white/10 rounded-lg p-3 text-white focus:border-teal-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Admin Password</label>
                    <input type="text" value={content.auth.password} onChange={(e) => updateContent('auth', 'password', e.target.value)} className="w-full bg-slate-900 border border-white/10 rounded-lg p-3 text-white focus:border-teal-500 focus:outline-none" />
                  </div>
                  <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl text-blue-400 text-sm">
                    <strong>Note:</strong> Since this is a serverless frontend application, credentials are saved in the local browser state. 
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

const CMSLoginScreen = ({ validCredentials, onLoginSuccess, onClose }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === validCredentials.username && password === validCredentials.password) {
      onLoginSuccess();
    } else {
      setError('Invalid username or password');
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/80 backdrop-blur-md animate-in fade-in zoom-in-95 duration-300 p-4">
      <div className="bg-slate-900 border border-white/10 rounded-3xl w-full max-w-4xl flex flex-col md:flex-row overflow-hidden shadow-[0_0_50px_rgba(20,184,166,0.15)] relative">
        <div className="w-full md:w-1/2 bg-gradient-to-br from-teal-900/80 to-blue-900/80 p-10 flex flex-col justify-center relative overflow-hidden hidden md:flex">
          <div className="absolute top-[-20%] left-[-20%] w-[80%] h-[80%] rounded-full bg-teal-500/20 blur-[80px] mix-blend-screen"></div>
          <div className="absolute bottom-[-20%] right-[-20%] w-[80%] h-[80%] rounded-full bg-blue-500/20 blur-[80px] mix-blend-screen"></div>
          <div className="relative z-10 space-y-6">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-md border border-white/20 shadow-xl"><ShieldCheck size={32} className="text-teal-400" /></div>
            <div>
              <h2 className="text-3xl font-extrabold text-white mb-2">Secure CMS Portal</h2>
              <p className="text-teal-100/70 leading-relaxed text-sm">Authenticate to access the CuRA AI operating system manager. Update brochure capabilities in real-time.</p>
            </div>
          </div>
        </div>
        <div className="w-full md:w-1/2 p-10 md:p-12 relative flex flex-col justify-center bg-slate-900">
          <button onClick={onClose} className="absolute top-6 right-6 text-slate-500 hover:text-white transition-colors bg-white/5 hover:bg-white/10 p-2 rounded-full"><X size={20} /></button>
          <div className="mb-8"><h3 className="text-2xl font-bold text-white mb-1">Admin Login</h3></div>
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Username</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><User size={16} className="text-slate-500" /></div>
                <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-xl py-3 pl-10 pr-4 text-white focus:border-teal-500 outline-none" placeholder="Enter admin username" autoFocus />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Key size={16} className="text-slate-500" /></div>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-xl py-3 pl-10 pr-4 text-white focus:border-teal-500 outline-none" placeholder="Enter password" />
              </div>
            </div>
            {error && <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-sm flex items-center gap-2"><ShieldAlert size={16} /> {error}</div>}
            <button type="submit" className="w-full py-3.5 bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-400 hover:to-blue-500 text-white rounded-xl font-bold text-sm transition-all shadow-[0_0_20px_rgba(45,212,191,0.3)] mt-4">Authenticate & Access CMS</button>
          </form>
        </div>
      </div>
    </div>
  );
};


// ============================================================================
// 4. MAIN BROCHURE APP WRAPPER
// ============================================================================

const BrochureApp = ({ onExit }) => {
  const [content, setContent] = useState(initialBrochureData);
  const [showCMS, setShowCMS] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [viewState, setViewState] = useState({ view: 'main', id: null });

  const { fontType = 'google', fontUrl, customFontUrl, fontFamily } = content.branding;

  useEffect(() => {
    const oldLink = document.getElementById('dynamic-google-font');
    if (oldLink) oldLink.remove();
    const oldStyle = document.getElementById('dynamic-custom-font-style');
    if (oldStyle) oldStyle.remove();

    if (fontType === 'google' && fontUrl) {
      const link = document.createElement('link');
      link.id = 'dynamic-google-font'; link.rel = 'stylesheet'; link.href = fontUrl;
      document.head.appendChild(link);
    }
  }, [fontType, fontUrl, customFontUrl, fontFamily]);

  return (
    <div className="min-h-screen bg-slate-900 relative overflow-x-hidden selection:bg-teal-500/30" style={{ fontFamily: `"${content.branding.fontFamily}", sans-serif` }}>
      {/* Liquid Glass Background Elements */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-teal-600/30 blur-[120px] mix-blend-screen"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[60vw] h-[60vw] rounded-full bg-blue-800/40 blur-[150px] mix-blend-screen"></div>
        <div className="absolute top-[40%] left-[60%] w-[30vw] h-[30vw] rounded-full bg-purple-600/20 blur-[100px] mix-blend-screen"></div>
      </div>

      {showCMS ? (
        <FullPageCMS content={content} setContent={setContent} onClose={() => setShowCMS(false)} />
      ) : (
        <>
          {showLogin && (
            <CMSLoginScreen validCredentials={content.auth} onLoginSuccess={() => { setShowLogin(false); setShowCMS(true); }} onClose={() => setShowLogin(false)} />
          )}

          {/* Navigation */}
          <nav className="relative z-20 px-6 py-4 flex justify-between items-center max-w-7xl mx-auto">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => setViewState({view: 'main'})}>
              {content.branding.logoUrl ? (
                <img src={content.branding.logoUrl} alt="Logo" className="h-10 object-contain" />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-blue-500 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">F</div>
              )}
              <span className="text-white font-bold text-xl tracking-tight">{content.branding.brandName}</span>
            </div>
            
            <button onClick={onExit} className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-bold border border-white/10 transition-colors flex items-center gap-2">
              <LogOut size={16} /> Exit Brochure
            </button>
          </nav>

          {/* --- CONDITIONAL VIEW ROUTING --- */}
          {viewState.view === 'feature' ? (
            <FeatureDetailView feature={content.deepDive.find(f => f.id === viewState.id)} brandName={content.branding.brandName} onBack={() => { setViewState({view: 'main'}); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
          ) : viewState.view === 'journey' ? (
            <JourneyDetailView journey={content.journey} onBack={() => { setViewState({view: 'main'}); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
          ) : (
            <main className="relative z-10 max-w-7xl mx-auto px-6 pb-24 transition-all duration-300">
              
              {/* Hero Section */}
              <div className="py-20 text-center max-w-4xl mx-auto">
                <h1 className="text-5xl md:text-7xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-white/70 mb-6 drop-shadow-sm leading-tight">
                  {content.hero.title}
                </h1>
                <h2 className="text-xl md:text-3xl font-medium text-teal-300 mb-6 drop-shadow">
                  {content.hero.subtitle}
                </h2>
                <p className="text-lg md:text-xl text-slate-300 mb-10 leading-relaxed max-w-3xl mx-auto">
                  {content.hero.description}
                </p>
              </div>

              {/* Executive Summary Matrix */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-24">
                {content.executiveSummary.map((item, idx) => (
                  <GlassCard key={idx} className="p-8 hover:bg-white/15 transition-colors duration-300 group">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white mb-6 shadow-lg group-hover:scale-110 transition-transform">
                      <IconResolver name={item.icon} className="w-7 h-7" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-6">{item.title}</h3>
                    <ul className="space-y-4">
                      {item.points.map((point, pIdx) => (
                        <li key={pIdx} className="flex items-start gap-3 text-slate-200">
                          <CheckCircle2 size={18} className="text-teal-400 shrink-0 mt-0.5" />
                          <span className="leading-tight">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </GlassCard>
                ))}
              </div>

              {/* Deep Dive Features with WhatsApp UI */}
              <div className="space-y-32 mb-24">
                <div className="text-center mb-16">
                  <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">Experience the Automation</h2>
                  <div className="w-24 h-1 bg-gradient-to-r from-teal-400 to-blue-500 mx-auto rounded-full"></div>
                </div>

                {content.deepDive.map((feature, idx) => (
                  <div key={feature.id} className={`flex flex-col md:flex-row gap-12 items-center ${idx % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}>
                    <div className="flex-1 space-y-6">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/20 border border-teal-500/30 text-teal-300 text-sm font-semibold">
                        Feature Module {idx + 1 < 10 ? `0${idx + 1}` : idx + 1}
                      </div>
                      <h3 className="text-4xl font-bold text-white leading-tight">{feature.title}</h3>
                      <p className="text-xl text-slate-300 leading-relaxed">{feature.desc}</p>
                      <button 
                        onClick={() => { setViewState({view: 'feature', id: feature.id}); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        className="mt-6 flex items-center gap-2 text-teal-400 font-bold hover:text-teal-300 transition-colors bg-teal-500/10 px-6 py-3 rounded-xl border border-teal-500/20 w-fit hover:bg-teal-500/20"
                      >
                        Explore Full Capabilities <ChevronRight size={20} />
                      </button>
                    </div>
                    <div className="flex-1 flex justify-center w-full relative">
                      <div className="absolute inset-0 bg-gradient-to-tr from-teal-500/30 to-blue-600/30 blur-3xl rounded-full scale-75 opacity-50"></div>
                      <BrochureWhatsAppMockup type={feature.phoneType} brandName={content.branding.brandName} />
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Patient Journey Overview */}
              <div className="mb-24">
                <div className="text-center mb-12">
                  <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">{content.journey.title}</h2>
                  <p className="text-slate-300 text-lg max-w-2xl mx-auto">{content.journey.subtitle}</p>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                  {content.journey.stages.map((stage, idx) => (
                    <GlassCard key={stage.id} className="p-6 relative group overflow-hidden border border-white/5 hover:border-blue-500/30 transition-all">
                      <div className="absolute top-0 left-0 w-1 h-full bg-blue-500/50 group-hover:bg-blue-400 transition-colors"></div>
                      <div className="text-blue-400 mb-4 bg-blue-500/10 w-12 h-12 rounded-full flex items-center justify-center">
                        <IconResolver name={stage.icon} className="w-6 h-6" />
                      </div>
                      <h4 className="text-lg font-bold text-white mb-2">Stage {idx+1}:<br/>{stage.name}</h4>
                      <p className="text-sm text-slate-400">{stage.brief}</p>
                    </GlassCard>
                  ))}
                </div>

                <div className="flex justify-center">
                  <button 
                    onClick={() => { setViewState({view: 'journey'}); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="flex items-center gap-2 text-white font-bold bg-blue-600 hover:bg-blue-500 transition-colors px-8 py-4 rounded-xl shadow-[0_0_20px_rgba(37,99,235,0.4)]"
                  >
                    <GitCommit size={20} /> Deep Dive into Journey Impact
                  </button>
                </div>
              </div>

              {/* ROI Calculator Section */}
              <div className="mb-24">
                 <ROICalculator />
              </div>

              {/* Implementation Roadmap Timeline */}
              <div className="mb-28">
                <div className="text-center mb-16">
                  <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">Implementation Roadmap</h2>
                  <div className="w-24 h-1 bg-gradient-to-r from-teal-400 to-blue-500 mx-auto rounded-full mb-4"></div>
                  <p className="text-slate-300 text-lg max-w-2xl mx-auto">A seamless, zero-hardware transition designed to minimize disruption to your clinic operations.</p>
                </div>

                <div className="relative max-w-4xl mx-auto px-4 mt-12">
                  <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-teal-500/10 via-blue-500/30 to-teal-500/10 -translate-x-1/2 rounded-full hidden md:block"></div>
                  <div className="absolute left-[38px] top-0 bottom-0 w-1 bg-gradient-to-b from-teal-500/10 via-blue-500/30 to-teal-500/10 rounded-full md:hidden"></div>

                  <div className="space-y-12">
                    {content.roadmap.map((stage, idx) => {
                      const isEven = idx % 2 === 0;
                      return (
                        <div key={idx} className={`relative flex md:justify-between flex-col md:flex-row items-start md:items-center ${isEven ? 'md:flex-row-reverse' : ''}`}>
                          <div className="absolute left-4 md:left-1/2 -translate-x-0 md:-translate-x-1/2 w-14 h-14 rounded-full bg-slate-900 border-4 border-teal-500 z-10 flex items-center justify-center text-teal-400 shadow-[0_0_20px_rgba(45,212,191,0.3)] md:static mt-2 md:mt-0 shrink-0 mx-auto">
                            <IconResolver name={stage.icon} className="w-6 h-6" />
                          </div>
                          <div className={`w-full md:w-[45%] pl-20 md:pl-0 ${isEven ? 'md:pr-12 md:text-right' : 'md:pl-12 text-left'}`}>
                            <GlassCard className={`p-6 hover:bg-white/15 transition-all duration-300 group`}>
                              <div className={`text-teal-400 font-bold text-xs mb-2 uppercase tracking-widest inline-block px-3 py-1 bg-teal-500/10 rounded-full border border-teal-500/20`}>
                                {stage.duration}
                              </div>
                              <h4 className="text-xl font-bold text-white mb-2 mt-2">{stage.phase}</h4>
                              <p className="text-slate-300 text-sm leading-relaxed">{stage.desc}</p>
                            </GlassCard>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Pricing Tiers Matrix */}
              <div className="mb-24">
                <div className="text-center mb-16">
                  <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">Tiered Architecture</h2>
                  <p className="text-slate-300 text-lg max-w-2xl mx-auto">Scale your patient engagement infrastructure matching your clinic's capacity.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {content.tiers.map((tier, idx) => (
                    <GlassCard key={idx} className={`p-8 relative overflow-hidden flex flex-col ${idx === 1 ? 'border-teal-400/50 shadow-[0_0_30px_rgba(45,212,191,0.2)]' : ''}`}>
                      {idx === 1 && <div className="absolute top-0 right-0 bg-teal-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">Most Popular</div>}
                      <h4 className="text-3xl font-bold text-white mb-1">{tier.name}</h4>
                      <p className="text-teal-300 font-medium mb-6">{tier.target}</p>
                      
                      <div className="mb-8 space-y-3">
                        <div className="p-4 bg-teal-500/10 rounded-xl border border-teal-500/20">
                           <span className="text-[10px] text-teal-400 uppercase tracking-widest block font-bold mb-1">One-time Setup Fee</span>
                           <div className="flex items-baseline gap-1">
                              <span className="text-white text-2xl font-bold">₹{tier.setupFee}</span>
                           </div>
                        </div>
                        <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
                           <span className="text-[10px] text-blue-400 uppercase tracking-widest block font-bold mb-1">Monthly License</span>
                           <div className="flex items-baseline gap-1">
                              <span className="text-white text-2xl font-bold">₹{tier.monthlyFee}</span>
                              <span className="text-slate-400 text-xs">/month</span>
                           </div>
                        </div>
                      </div>

                      <ul className="space-y-4 flex-1 mb-8">
                        {tier.features.map((feat, fIdx) => (
                          <li key={fIdx} className="flex items-start gap-3 text-slate-200">
                            <CheckCircle2 size={16} className="text-teal-400 shrink-0 mt-0.5" />
                            <span className="text-sm leading-tight">{feat}</span>
                          </li>
                        ))}
                      </ul>

                      <button className={`w-full py-3 rounded-xl font-bold transition-all ${idx === 1 ? 'bg-teal-500 hover:bg-teal-400 text-white shadow-lg shadow-teal-500/20' : 'bg-white/10 hover:bg-white/20 text-white'}`}>
                        Select {tier.name}
                      </button>
                    </GlassCard>
                  ))}
                </div>
              </div>
              
            </main>
          )}

          {/* Global Footer */}
          <footer className="relative z-20 max-w-7xl mx-auto w-full px-6 py-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center text-slate-400 text-sm">
            <p>&copy; 2026 Canvas Future. Direct-to-Meta Enterprise Solutions.</p>
            <button onClick={() => setShowLogin(true)} className="mt-4 md:mt-0 px-3 py-1.5 rounded-lg font-medium text-xs transition-all flex items-center gap-2 bg-transparent text-slate-600 hover:bg-white/5 hover:text-slate-300 border border-transparent hover:border-white/10">
              <Edit3 size={14} /> Edit Brochure
            </button>
          </footer>
        </>
      )}
    </div>
  );
};


// ============================================================================
// 5. DEMO WORKBENCH APPLICATION WRAPPER
// ============================================================================

const DemoWorkbench = ({ onExit }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null); 
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const [activeTab, setActiveTab] = useState('dashboard');
  const [isDeviceOpen, setIsDeviceOpen] = useState(true);
  
  const [inputText, setInputText] = useState('');
  const [adminInputText, setAdminInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [escalations, setEscalations] = useState([]);
  const [toastMsg, setToastMsg] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [selectedPatients, setSelectedPatients] = useState(['sj_1']);
  
  const chatEndRef = useRef(null);
  const adminChatEndRef = useRef(null);

  const mockPatients = [
    { id: 'sj_1', name: 'Sarah Jenkins', phone: '+91 98*** **210', status: 'Active Cycle' },
    { id: 'ps_2', name: 'Priya Sharma', phone: '+91 98*** **111', status: 'Consultation' },
    { id: 'ad_3', name: 'Anjali Desai', phone: '+91 98*** **222', status: 'TWW Day 3' },
    { id: 'kr_4', name: 'Kavita Rao', phone: '+91 98*** **333', status: 'Ovarian Stimulation' }
  ];

  const getInitialMessages = () => [
    { id: 1, sender: 'system', text: 'CuRA AI - Official Business Account secured by Canvas Future.', timestamp: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) },
    { id: 2, sender: 'bot', text: 'Hi Sarah! Welcome to Canvas Future Fertility. I am CuRA, your 24/7 digital care coordinator. How can I assist you today? 🌸', timestamp: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
      options: [
        { label: '📅 Book Appointment', action: () => handleUserSend('I want to book an appointment.', true) },
        { label: '📋 My Treatment Plan', action: () => handleUserSend('What is my current treatment plan?', true) },
        { label: '🚨 Report Symptom', action: () => handleUserSend('I need to report a medical symptom.', true) },
        { label: '🏥 Clinic Info', action: () => handleUserSend('What are your hours and location?', true) }
      ]
    }
  ];

  const [messages, setMessages] = useState(getInitialMessages());

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    adminChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, activeTab]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === 'super' && password === 'super123') { setUserRole('superadmin'); setIsLoggedIn(true); setActiveTab('dashboard'); }
    else if (username === 'admin' && password === 'admin123') { setUserRole('admin'); setIsLoggedIn(true); setActiveTab('dashboard'); }
    else if (username === 'patient' && password === 'patient123') { setUserRole('patient'); setIsLoggedIn(true); setIsDeviceOpen(true); }
    else { setLoginError('Invalid credentials.'); }
  };

  const handleLogout = () => {
    setIsLoggedIn(false); setUserRole(null); setUsername(''); setPassword('');
    setMessages(getInitialMessages()); setEscalations([]);
  };

  const checkRedFlags = (text) => {
    const redFlags = ['bleeding', 'shortness of breath', 'severe bloating', 'pain', 'faint', 'symptom'];
    const lowerText = text.toLowerCase();
    if (redFlags.some(flag => lowerText.includes(flag))) {
      setEscalations(prev => [...prev, { id: Date.now(), patient: 'Sarah Jenkins', message: text, time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}), status: 'Active' }]);
    }
  };

  const addMessage = (sender, text, options = null, metadata = null) => {
    setMessages(prev => [...prev, { id: Date.now(), sender, text, options, metadata, timestamp: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) }]);
  };

  const sendMessageToGemini = async (userMessage) => {
    setIsTyping(true);
    
    if (!apiKey || apiKey.trim() === "") {
      addMessage('bot', '⚠️ AI Counselor is offline. Please enter your Gemini API Key in the source code (line 22) to enable dynamic responses.');
      setIsTyping(false);
      return;
    }

    // Hardcoded back to gemini-2.5-flash
    const modelVersion = 'gemini-2.5-flash';
    const dynamicApiUrl = `https://generativelanguage.googleapis.com/v1beta/models/${modelVersion}:generateContent?key=${apiKey.trim()}`;
    
    const history = messages.filter(m => m.sender === 'user' || m.sender === 'bot').map(m => `${m.sender === 'user' ? 'Sarah' : 'CuRA'}: ${m.text}`).join('\n');
    const prompt = `${SYSTEM_PROMPT}\n\nRecent chat history:\n${history}\n\nSarah's new message: "${userMessage}"\nRespond directly as CuRA.`;
    
    try {
      let data, retryCount = 0;
      let lastError = "";
      while (retryCount < 3) {
        const response = await fetch(dynamicApiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] }) });
        data = await response.json();
        if (data.candidates) break;
        if (data.error) {
            lastError = data.error.message;
            if (lastError.includes("limit: 0")) {
                lastError = "Free tier quota limit is 0 in your region/account. Please enable billing in Google AI Studio / Cloud Console to use the API.";
            }
        }
        retryCount++;
        await new Promise(r => setTimeout(r, 1000 * Math.pow(2, retryCount)));
      }
      if (data.candidates) {
        addMessage('bot', data.candidates[0].content.parts[0].text);
      } else {
        addMessage('bot', `⚠️ API Error: ${lastError || 'Invalid API key or service unavailable.'}`);
      }
    } catch (error) {
      addMessage('bot', `⚠️ Network Error: ${error.message}`);
    } finally { setIsTyping(false); }
  };

  const handleUserSend = (overrideText = null, isQuickReply = false) => {
    const text = overrideText || inputText;
    if (!text.trim()) return;
    addMessage('user', text); setInputText(''); checkRedFlags(text);
    setTimeout(() => sendMessageToGemini(text), isQuickReply ? 600 : 0);
  };

  const handleAdminSend = () => {
    if (!adminInputText.trim()) return;
    addMessage('admin', adminInputText);
    setAdminInputText('');
  };

  const triggerToast = (msg) => { setToastMsg(msg); setTimeout(() => setToastMsg(null), 3000); };
  const pushIntakeForm = () => { addMessage('bot', 'Please fill out our digital intake form to secure your EMR profile.', [{ label: '📝 Start Digital Intake', action: () => handleUserSend('Starting intake...', true) }]); setActiveModal(null); triggerToast('Intake form sent successfully'); };
  const pushTriggerShot = () => { addMessage('bot', '🚨 CRITICAL REMINDER 🚨\nYour Ovitrelle trigger shot is scheduled for exactly 10:00 PM tonight. Please tap confirm once administered.', [{ label: '✅ Confirm Dose Taken', action: () => { handleUserSend('I have taken the dose.', true); setTimeout(() => addMessage('bot', 'Dose confirmed. Great job!'), 1000); }}, { label: '📹 Watch How-to Video', action: () => handleUserSend('Show me the video', true) }]); setActiveModal(null); triggerToast('Trigger shot reminder broadcasted'); };
  const pushLabReport = () => { addMessage('bot', '📄 Your recent Beta hCG blood work results are ready.', null, { isPdf: true, filename: 'Beta_hCG_Sarah.pdf' }); setActiveModal(null); triggerToast('Lab report delivered securely'); };
  const pushTWWCheckin = () => { addMessage('bot', 'Hi Sarah, checking in on Day 5 of your Two-Week Wait (TWW). How are you feeling physically and mentally today? 🌟', [{ label: 'Feeling Good!', action: () => handleUserSend('I am feeling good, just a bit anxious.', true) }, { label: 'Having Symptoms', action: () => handleUserSend('I am having some bloating and mild cramps.', true) }]); setActiveModal(null); triggerToast('TWW support module activated'); };

  const renderSidebar = () => (
    <div className="w-64 bg-[#0f172a] text-white flex flex-col hidden md:flex flex-shrink-0">
      <div className="p-5 flex items-center gap-3 border-b border-slate-800">
        <div className="bg-emerald-500 p-1.5 rounded-lg text-white"><Activity size={22} /></div>
        <h1 className="font-bold text-xl tracking-tight">CuRA AI</h1>
      </div>
      <div className="flex-1 py-6 flex flex-col gap-2 px-3">
        <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Platform</p>
        <button onClick={() => setActiveTab('dashboard')} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === 'dashboard' ? 'bg-emerald-500/10 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'}`}><LayoutDashboard size={18} /> Dashboard</button>
        <button onClick={() => setActiveTab('inbox')} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === 'inbox' ? 'bg-emerald-500/10 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'}`}><MessageSquare size={18} /> Shared Inbox <span className="ml-auto bg-emerald-500 text-white text-[10px] px-2 py-0.5 rounded-full">New</span></button>
        <button onClick={() => setActiveTab('patients')} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === 'patients' ? 'bg-emerald-500/10 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'}`}><Users size={18} /> Directory</button>
        
        {(userRole === 'superadmin' || userRole === 'admin') && (
          <>
            <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mt-6 mb-2">Management</p>
            {userRole === 'superadmin' && (
              <button onClick={() => setActiveTab('billing')} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === 'billing' ? 'bg-emerald-500/10 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'}`}><CreditCard size={18} /> Billing & Plan</button>
            )}
            <button onClick={() => setActiveTab('settings')} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === 'settings' ? 'bg-emerald-500/10 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'}`}><Settings size={18} /> Settings</button>
          </>
        )}
      </div>
      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center font-bold text-sm uppercase">{userRole.substring(0,2)}</div>
          <div><p className="text-sm font-semibold capitalize">{userRole}</p><p className="text-xs text-slate-400">Canvas Future Clinic</p></div>
        </div>
        <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-red-400 text-sm w-full py-2 transition-colors mb-2"><LogOut size={16} /> Secure Logout</button>
        <button onClick={onExit} className="flex items-center gap-2 text-slate-400 hover:text-blue-400 text-sm w-full py-2 transition-colors"><ArrowLeft size={16} /> Back to Hub</button>
      </div>
    </div>
  );

  const renderDashboard = () => (
    <div className="p-6 md:p-8 animate-in fade-in max-w-7xl mx-auto">
      <div className="flex justify-between items-end mb-8">
        <div><h2 className="text-2xl font-bold text-slate-800">Clinic Command Center</h2><p className="text-slate-500 text-sm mt-1">Real-time overview of IVF patient interactions and AI automation.</p></div>
        <div className="flex gap-2">
           <button onClick={() => setActiveModal('trigger')} className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition flex items-center gap-2"><Bell size={16} /> Broadcast Trigger</button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {[{ label: 'Active Cycles Tracked', value: '142', trend: '+12%', icon: Activity, color: 'emerald' }, { label: 'Front-Desk Calls Saved', value: '840', trend: '-60%', icon: Phone, color: 'blue' }, { label: 'Active Escalations', value: escalations.length, trend: 'Needs Action', icon: ShieldAlert, color: escalations.length > 0 ? 'red' : 'gray' }, { label: 'Appt. No-Show Rate', value: '2.4%', trend: '-15%', icon: Calendar, color: 'indigo' }].map((stat, i) => (
          <div key={i} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
            <div className={`w-10 h-10 rounded-lg bg-${stat.color}-100 text-${stat.color}-600 flex items-center justify-center mb-3`}><stat.icon size={20} /></div>
            <h3 className="text-slate-500 text-sm font-medium">{stat.label}</h3>
            <div className="flex items-end gap-3 mt-1">
              <span className="text-2xl font-bold text-slate-800">{stat.value}</span>
              <span className={`text-xs font-medium mb-1 ${stat.trend.includes('+') ? 'text-emerald-500' : stat.trend.includes('-') ? 'text-blue-500' : 'text-red-500'}`}>{stat.trend}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col">
          <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50 rounded-t-xl">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><AlertTriangle size={18} className="text-amber-500" /> Clinical Triage Alerts</h3>
            <span className="bg-red-100 text-red-700 text-xs px-2.5 py-1 rounded-full font-bold">{escalations.length} Active</span>
          </div>
          <div className="p-5 flex-1 overflow-y-auto">
            {escalations.length === 0 ? (
              <div className="text-center py-10 text-slate-400"><CheckCircle2 size={40} className="mx-auto text-emerald-200 mb-3" /><p>All patients stable. No critical NLP flags.</p></div>
            ) : (
              <div className="space-y-4">
                {escalations.map((esc) => (
                  <div key={esc.id} className="bg-red-50/50 border border-red-100 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2"><span className="font-semibold text-slate-800">{esc.patient}</span><span className="text-xs text-slate-500">{esc.time}</span></div>
                    <p className="text-sm text-red-800 bg-white p-2 rounded border border-red-100 mb-3">"{esc.message}"</p>
                    <div className="flex gap-2">
                      <button onClick={() => setActiveTab('inbox')} className="text-xs bg-red-600 text-white px-3 py-1.5 rounded hover:bg-red-700 transition">View Chat</button>
                      <button onClick={() => { setEscalations(escalations.filter(e => e.id !== esc.id)); triggerToast('Alert resolved'); }} className="text-xs bg-white border border-slate-300 text-slate-600 px-3 py-1.5 rounded hover:bg-slate-50 transition">Resolve</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-5 border-b border-slate-100 bg-slate-50 rounded-t-xl"><h3 className="font-semibold text-slate-800 flex items-center gap-2"><Activity size={18} className="text-blue-500" /> Active Drip Campaigns</h3></div>
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between p-3 border border-slate-100 rounded-lg hover:border-emerald-200 hover:bg-emerald-50/30 transition cursor-pointer" onClick={() => setActiveModal('intake')}>
              <div className="flex items-center gap-3"><div className="bg-emerald-100 p-2 rounded text-emerald-600"><FileText size={18} /></div><div><p className="font-medium text-sm text-slate-800">Digital Intake Flow</p><p className="text-xs text-slate-500">Push EMR sync registration</p></div></div><ChevronRight size={16} className="text-slate-400" />
            </div>
            <div className="flex items-center justify-between p-3 border border-slate-100 rounded-lg hover:border-blue-200 hover:bg-blue-50/30 transition cursor-pointer" onClick={() => setActiveModal('report')}>
              <div className="flex items-center gap-3"><div className="bg-blue-100 p-2 rounded text-blue-600"><Activity size={18} /></div><div><p className="font-medium text-sm text-slate-800">Secure Report Delivery</p><p className="text-xs text-slate-500">Send password-protected PDFs</p></div></div><ChevronRight size={16} className="text-slate-400" />
            </div>
             <div className="flex items-center justify-between p-3 border border-slate-100 rounded-lg hover:border-purple-200 hover:bg-purple-50/30 transition cursor-pointer" onClick={() => setActiveModal('tww')}>
              <div className="flex items-center gap-3"><div className="bg-purple-100 p-2 rounded text-purple-600"><Smile size={18} /></div><div><p className="font-medium text-sm text-slate-800">TWW Support Module</p><p className="text-xs text-slate-500">Trigger anxiety-reduction flow</p></div></div><ChevronRight size={16} className="text-slate-400" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSharedInbox = () => (
    <div className="flex h-full animate-in fade-in">
      <div className="w-80 border-r border-slate-200 bg-white flex flex-col hidden lg:flex">
        <div className="p-4 border-b border-slate-200"><div className="bg-slate-100 rounded-lg flex items-center px-3 py-2"><Search size={16} className="text-slate-400" /><input type="text" placeholder="Search patients..." className="bg-transparent border-none focus:ring-0 text-sm ml-2 w-full outline-none" /></div></div>
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 border-b border-slate-100 bg-blue-50/50 cursor-pointer flex gap-3 relative"><div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500"></div><div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex justify-center items-center font-bold">SJ</div><div className="flex-1 min-w-0"><div className="flex justify-between items-center mb-1"><h4 className="font-semibold text-slate-800 text-sm">Sarah Jenkins</h4><span className="text-xs text-slate-500">Now</span></div><p className="text-xs text-slate-600 truncate">{messages[messages.length-1]?.text || 'No messages'}</p></div></div>
          {['Priya Sharma', 'Anjali Desai', 'Kavita Rao'].map((name, i) => (<div key={i} className="p-4 border-b border-slate-100 cursor-pointer flex gap-3 hover:bg-slate-50 transition opacity-60"><div className="w-10 h-10 rounded-full bg-slate-100 text-slate-600 flex justify-center items-center font-bold">{name.split(' ').map(n=>n[0]).join('')}</div><div className="flex-1 min-w-0"><div className="flex justify-between items-center mb-1"><h4 className="font-semibold text-slate-800 text-sm">{name}</h4><span className="text-xs text-slate-400">Yesterday</span></div><p className="text-xs text-slate-500 truncate">CuRA: Please review your scan results.</p></div></div>))}
        </div>
      </div>
      <div className="flex-1 flex flex-col bg-slate-50">
        <div className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 shadow-sm z-10">
          <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex justify-center items-center font-bold">SJ</div><div><h3 className="font-bold text-slate-800">Sarah Jenkins</h3><p className="text-xs text-emerald-600 font-medium flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> AI Handling</p></div></div>
          <div className="flex gap-2"><button onClick={() => setActiveModal('patientProfile')} className="text-sm bg-white border border-slate-300 text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-50 font-medium transition">Patient Profile</button><button className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 font-medium shadow-sm transition">Takeover Chat</button></div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
           <div className="text-center"><span className="bg-slate-200 text-slate-600 text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-full">Today</span></div>
           {messages.map((msg) => {
             if (msg.sender === 'system') return null;
             const isPatient = msg.sender === 'user';
             const isHumanAgent = msg.sender === 'admin';
             return (
               <div key={msg.id} className={`flex ${isPatient ? 'justify-start' : 'justify-end'}`}>
                 <div className={`max-w-[70%] rounded-2xl p-4 shadow-sm ${isPatient ? 'bg-white border border-slate-200 rounded-tl-sm' : isHumanAgent ? 'bg-blue-600 text-white rounded-tr-sm' : 'bg-emerald-50 border border-emerald-100 rounded-tr-sm'}`}>
                   <div className="flex items-center gap-2 mb-1.5 opacity-80">{isPatient ? <User size={12}/> : isHumanAgent ? <Users size={12}/> : <Activity size={12}/>}<span className="text-[11px] font-bold uppercase">{isPatient ? 'Sarah' : isHumanAgent ? 'Dr. Sharma (Human)' : 'CuRA AI'}</span></div>
                   {msg.metadata?.isPdf && (<div className="bg-white/80 rounded p-2 mb-2 flex items-center gap-3 border border-black/10"><FileText size={20} className={isHumanAgent ? 'text-blue-200' : 'text-red-500'} /><span className="text-sm font-semibold truncate">{msg.metadata.filename}</span></div>)}
                   <p className={`text-sm whitespace-pre-wrap ${isHumanAgent ? 'text-white' : 'text-slate-800'}`}>{msg.text}</p>
                   <div className={`text-[10px] text-right mt-2 ${isHumanAgent ? 'text-blue-200' : 'text-slate-400'}`}>{msg.timestamp}</div>
                 </div>
               </div>
             )
           })}
           <div ref={adminChatEndRef} />
        </div>
        <div className="p-4 bg-white border-t border-slate-200">
          <div className="bg-slate-100 rounded-xl flex items-end px-4 py-2 border border-slate-200 focus-within:border-blue-400 transition-colors">
            <textarea value={adminInputText} onChange={(e) => setAdminInputText(e.target.value)} placeholder="Type message to Sarah... (This will pause CuRA AI)" className="flex-1 bg-transparent border-none outline-none resize-none py-2 text-sm text-slate-800 max-h-32" rows={1} onKeyDown={(e) => { if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAdminSend(); } }} />
            <div className="flex items-center gap-2 pb-2"><button className="text-slate-400 hover:text-slate-600"><Paperclip size={18}/></button><button onClick={handleAdminSend} className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 shadow-sm"><Send size={16}/></button></div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBilling = () => (
    <div className="p-6 md:p-8 animate-in fade-in max-w-5xl mx-auto">
       <div className="mb-8">
        <h2 className="text-2xl font-bold text-slate-800">Subscription & Billing</h2>
        <p className="text-slate-500 text-sm mt-1">Manage your CuRA AI plan according to the Tiered Product Matrix.</p>
      </div>

      <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5 flex items-center justify-between mb-8 shadow-sm">
        <div>
          <span className="bg-indigo-200 text-indigo-800 text-xs font-bold px-2 py-1 rounded mb-2 inline-block uppercase">Current Plan</span>
          <h3 className="text-xl font-bold text-indigo-900">Basic Tier (Mid-Sized Centers)</h3>
          <p className="text-sm text-indigo-700 mt-1">Up to 50 Templates • API Push Sync • Standard IVF/IUI Flows</p>
        </div>
        <button className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium shadow hover:bg-indigo-700 transition">Upgrade to Advanced</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
         {[
           { name: 'Lite', target: 'Boutique Clinics', price: '₹16,000/mo', features: ['Up to 15 Templates', 'PDFs & Images Only', 'Standard 24h Reminders', 'Exportable CSV Sync', 'English + 1 Regional Lang'] },
           { name: 'Basic', target: 'Mid-Sized Centers', price: '₹25,000/mo', active: true, features: ['Up to 50 Templates', 'Video & Protected PDFs', 'Dynamic Doctor Alerts', 'Standard API Push', 'Standard IVF/IUI Flows', 'English + 2 Regional'] },
           { name: 'Advanced', target: 'Enterprise Networks', price: '₹45,000/mo', features: ['Unlimited Templates', 'Interactive Custom Docs', 'Predictive Rebooking Engine', 'Native EMR Bi-directional Sync', 'Custom Protocol Mapping', 'Omnilingual Translation'] }
         ].map((tier) => (
           <div key={tier.name} className={`bg-white rounded-xl border ${tier.active ? 'border-indigo-400 shadow-md ring-2 ring-indigo-50' : 'border-slate-200'} p-6 relative`}>
             {tier.active && <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-indigo-500 text-white text-xs font-bold px-3 py-1 rounded-full">Active Plan</div>}
             <h3 className="text-lg font-bold text-slate-800">{tier.name}</h3>
             <p className="text-xs text-slate-500 mb-4">{tier.target}</p>
             <div className="text-2xl font-bold text-slate-900 mb-6">{tier.price}</div>
             <ul className="space-y-3 mb-6">
               {tier.features.map((feat, i) => (
                 <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                   <CheckCheck size={16} className="text-emerald-500 shrink-0 mt-0.5" /> <span>{feat}</span>
                 </li>
               ))}
             </ul>
           </div>
         ))}
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="p-6 md:p-8 animate-in fade-in max-w-5xl mx-auto space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-slate-800">Platform Settings</h2>
        <p className="text-slate-500 text-sm mt-1">Manage clinic branding and general platform configurations.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><ImageIcon size={18} className="text-blue-500" /> Clinic Branding</h3>
        <div className="flex items-start gap-6">
           <div className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center text-slate-400 bg-slate-50 hover:bg-slate-100 cursor-pointer transition">
             <Upload size={24} className="mb-2" />
             <span className="text-xs font-medium">Upload Logo</span>
           </div>
           <div className="flex-1">
             <p className="text-sm text-slate-600 mb-3">This logo will appear in the WhatsApp header for your patients and across the patient portal. Recommended size: 256x256px.</p>
             <div className="flex gap-3">
               <button className="bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-50 transition">Choose File</button>
               <button className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 shadow-sm transition">Save Changes</button>
             </div>
           </div>
        </div>
      </div>
    </div>
  );

  const renderDirectory = () => (
    <div className="p-6 md:p-8 animate-in fade-in max-w-5xl mx-auto space-y-8">
      <div className="text-center text-slate-500 mt-10 mb-12">
        <Users size={48} className="mx-auto mb-4 text-slate-300" />
        <h3 className="text-xl font-bold text-slate-700">Patient Directory</h3>
        <p>This module connects to your EMR (e.g. via HL7/FHIR) as per the architectural pillars.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><UserPlus size={18} className="text-emerald-500" /> Manual Patient Registration (No EMR)</h3>
        <p className="text-sm text-slate-600 mb-4">Add patients directly to CuRA AI if you are bypassing native EMR integration.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Full Name</label>
            <input type="text" placeholder="e.g. Anjali Desai" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400 transition" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">WhatsApp Number</label>
            <input type="tel" placeholder="+91 98765 43210" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400 transition" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Treatment Stage</label>
            <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400 transition bg-white">
              <option>Consultation Phase</option>
              <option>Ovarian Stimulation</option>
              <option>Two-Week Wait (TWW)</option>
              <option>Post-Treatment / Follow-up</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Primary Physician</label>
            <input type="text" placeholder="e.g. Dr. Sharma" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400 transition" />
          </div>
        </div>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm transition flex items-center gap-2">
          <Plus size={16} /> Add Patient Record
        </button>
      </div>
    </div>
  );

  const renderModals = () => {
    if (!activeModal) return null;
    
    if (activeModal === 'patientProfile') {
      return (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h3 className="font-bold text-slate-800 flex items-center gap-2"><User size={18} className="text-blue-500"/> Patient Profile: Sarah Jenkins</h3>
              <button onClick={() => setActiveModal(null)} className="text-slate-400 hover:text-slate-600"><X size={20}/></button>
            </div>
            <div className="p-6 flex flex-col md:flex-row gap-6">
               <div className="w-full md:w-1/3 space-y-4">
                  <div className="flex items-center gap-3">
                     <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-600 flex justify-center items-center font-bold text-xl shrink-0">SJ</div>
                     <div><h4 className="font-bold text-slate-800">Sarah Jenkins</h4><p className="text-xs text-slate-500">EMR ID: PT-88219</p></div>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 space-y-2 text-sm">
                    <p><span className="text-slate-500 font-medium">Age:</span> 34</p><p><span className="text-slate-500 font-medium">Phone:</span> +91 98*** **210</p><p><span className="text-slate-500 font-medium">Partner:</span> Mark Jenkins</p><p><span className="text-slate-500 font-medium">Location:</span> Bengaluru, KA</p>
                  </div>
               </div>
               <div className="w-full md:w-2/3 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="border border-slate-100 rounded-lg p-3 bg-white shadow-sm"><p className="text-[10px] uppercase font-bold text-slate-400 mb-1">Current Stage</p><p className="font-semibold text-emerald-600 flex items-center gap-1"><Activity size={14}/> Active Cycle (IVF)</p></div>
                    <div className="border border-slate-100 rounded-lg p-3 bg-white shadow-sm"><p className="text-[10px] uppercase font-bold text-slate-400 mb-1">Primary Doctor</p><p className="font-semibold text-slate-800">Dr. Sharma</p></div>
                  </div>
                  <div className="border border-slate-100 rounded-lg overflow-hidden">
                    <div className="bg-slate-50 px-3 py-2 border-b border-slate-100 font-semibold text-sm text-slate-700">Treatment Summary</div>
                    <div className="p-3 text-sm text-slate-600 space-y-3">
                       <p className="flex justify-between items-center"><span className="text-slate-500 font-medium">Protocol:</span> <span className="font-semibold bg-slate-100 px-2 py-0.5 rounded text-slate-700">Antagonist Protocol</span></p>
                       <p className="flex justify-between items-center"><span className="text-slate-500 font-medium">Next Action:</span> <span className="font-semibold text-purple-600">Trigger Shot - Today, 10:00 PM</span></p>
                       <p className="flex justify-between items-center"><span className="text-slate-500 font-medium">Last Lab:</span> <span className="font-medium text-blue-600 hover:underline cursor-pointer flex items-center gap-1"><FileText size={14}/> Beta hCG (Normal)</span></p>
                       <p className="flex justify-between items-center"><span className="text-slate-500 font-medium">Next Appt:</span> <span className="font-medium text-slate-800 flex items-center gap-1"><Calendar size={14}/> Retrieval - Tmrw 8:00 AM</span></p>
                    </div>
                  </div>
               </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button onClick={() => setActiveModal(null)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200 bg-white border border-slate-300 rounded-lg transition">Close Profile</button>
              <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition">Sync / Edit in EMR</button>
            </div>
          </div>
        </div>
      );
    }

    let title, desc, action, btnText, btnColor;
    if (activeModal === 'intake') { title = "Digital Intake Flow"; desc = "Send a secure intake form directly to WhatsApp. Syncs with EMR."; action = pushIntakeForm; btnText = "Send Intake Form"; btnColor = "bg-emerald-600 hover:bg-emerald-700"; }
    else if (activeModal === 'report') { title = "Secure Report Delivery"; desc = "Deliver password-protected lab results directly via WhatsApp."; action = pushLabReport; btnText = "Send PDF Report"; btnColor = "bg-blue-600 hover:bg-blue-700"; }
    else if (activeModal === 'tww') { title = "TWW Support Module"; desc = "Trigger the automated Two-Week Wait check-in campaign."; action = pushTWWCheckin; btnText = "Start TWW Campaign"; btnColor = "bg-purple-600 hover:bg-purple-700"; }
    else if (activeModal === 'trigger') { title = "Trigger Shot Reminder"; desc = "Broadcast an urgent, interactive reminder for trigger injections."; action = pushTriggerShot; btnText = "Broadcast Reminder"; btnColor = "bg-red-600 hover:bg-red-700"; }

    const handleBroadcast = () => {
      if (selectedPatients.length === 0) { triggerToast('Please select at least one patient'); return; }
      action();
    };

    return (
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
          <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50"><h3 className="font-bold text-slate-800">{title}</h3><button onClick={() => setActiveModal(null)} className="text-slate-400 hover:text-slate-600"><X size={20}/></button></div>
          <div className="p-6">
            <p className="text-sm text-slate-600 mb-6 leading-relaxed">{desc}</p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg mb-6 flex flex-col max-h-52 overflow-hidden shadow-sm">
              <div className="p-3 border-b border-slate-200 bg-white flex justify-between items-center sticky top-0"><p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Select Recipients</p><label className="flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer"><input type="checkbox" className="rounded border-slate-300 text-emerald-600" checked={selectedPatients.length === mockPatients.length} onChange={(e) => e.target.checked ? setSelectedPatients(mockPatients.map(p => p.id)) : setSelectedPatients([])} /> Select All</label></div>
              <div className="overflow-y-auto p-2 space-y-1 custom-scrollbar">
                {mockPatients.map(patient => (
                  <label key={patient.id} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition border border-transparent ${selectedPatients.includes(patient.id) ? 'bg-emerald-50/50 border-emerald-100' : 'hover:bg-slate-100'}`}>
                    <input type="checkbox" className="rounded border-slate-300 text-emerald-600" checked={selectedPatients.includes(patient.id)} onChange={(e) => e.target.checked ? setSelectedPatients(prev => [...prev, patient.id]) : setSelectedPatients(prev => prev.filter(id => id !== patient.id))} />
                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex justify-center items-center font-bold text-xs shrink-0">{patient.name.split(' ').map(n=>n[0]).join('')}</div>
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-800 leading-none mb-1">{patient.name}</p><p className="text-[10px] text-slate-500 truncate">{patient.phone} • <span className="font-medium text-slate-600">{patient.status}</span></p></div>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-3 justify-end items-center">
              <span className="text-xs text-slate-500 mr-auto font-medium">{selectedPatients.length} selected</span>
              <button onClick={() => setActiveModal(null)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition">Cancel</button>
              <button onClick={handleBroadcast} className={`px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm transition ${btnColor}`}>{btnText}</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDevicePreview = () => (
    <div className={`transition-all duration-300 ease-in-out border-l border-slate-200 bg-[#fdfcfb] relative flex flex-col justify-center items-center overflow-y-auto shadow-[-10px_0_15px_-3px_rgba(0,0,0,0.05)] ${isDeviceOpen || userRole === 'patient' ? 'w-[400px] min-w-[360px] opacity-100' : 'w-0 min-w-0 opacity-0 overflow-hidden'} ${userRole === 'patient' ? '!w-full flex-1' : ''}`}>
      {userRole !== 'patient' && (
        <div className="absolute top-4 w-full flex justify-between px-6 items-center"><span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1"><Activity size={12}/> Live Patient Preview</span><button onClick={() => setIsDeviceOpen(false)} className="bg-slate-100 p-1.5 rounded-full hover:bg-slate-200 text-slate-500 transition"><X size={16}/></button></div>
      )}
      <div className="relative w-[320px] h-[660px] flex-shrink-0 bg-black rounded-[3rem] p-1.5 shadow-2xl border-[2px] border-gray-800 my-auto">
        <div className="absolute top-28 -left-1 w-1 h-10 bg-gray-800 rounded-l-md"></div><div className="absolute top-40 -left-1 w-1 h-10 bg-gray-800 rounded-l-md"></div><div className="absolute top-32 -right-1 w-1 h-14 bg-gray-800 rounded-r-md"></div>
        <div className="w-full h-full bg-[#EFE6DD] rounded-[2.6rem] overflow-hidden relative flex flex-col">
          <div className="absolute top-2 inset-x-0 mx-auto w-[90px] h-[26px] bg-black rounded-full z-40 flex items-center justify-between px-2 shadow-sm"><div className="w-2 h-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-full opacity-80"></div><div className="w-2 h-2 bg-emerald-500 rounded-full opacity-30 blur-[2px]"></div></div>
          <div className="h-10 w-full pt-3 px-6 flex justify-between items-center text-[12px] font-bold z-30 bg-transparent absolute top-0 text-black"><span className="tracking-tight mt-0.5">18:18</span><div className="flex gap-1.5 items-center mr-0 mt-0.5"><Signal size={14} className="fill-current" strokeWidth={2.5} /><Wifi size={14} className="fill-current" strokeWidth={2.5} /><Battery size={16} className="fill-current opacity-90" /></div></div>
          <div className="bg-[#f6f6f6]/95 backdrop-blur-md text-black px-2 pt-11 pb-2 flex items-center justify-between z-20 border-b border-gray-300">
            <div className="flex items-center gap-1">
              <button className="text-[#007AFF] flex items-center"><ChevronLeft size={28} strokeWidth={2.5} /></button>
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden border border-gray-300 ml-0.5"><User size={20} className="text-gray-400" fill="currentColor" /></div>
              <div className="ml-2 flex flex-col justify-center"><h2 className="font-semibold text-[15px] flex items-center gap-1 leading-none mb-1">Canvas Future <div className="bg-[#00A884] rounded-full w-[12px] h-[12px] flex items-center justify-center"><CheckCheck size={8} className="text-white" strokeWidth={3} /></div></h2><p className="text-[11px] text-gray-500 leading-none">tap here for contact info</p></div>
            </div>
            <div className="flex items-center gap-3 text-[#007AFF] pr-3"><Video size={24} strokeWidth={1.5} /><Phone size={22} strokeWidth={1.5} /></div>
          </div>
          <div className="flex-1 bg-[#EFE6DD] p-3 overflow-y-auto relative custom-scrollbar flex flex-col gap-3" style={{backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 10h10v10H10zM30 30h10v10H30z' fill='%23000000' fill-opacity='0.035' fill-rule='evenodd'/%3E%3C/svg%3E")`, backgroundSize: '120px'}}>
            <div className="flex justify-center mt-2 mb-1"><span className="bg-[#f0f2f5]/80 backdrop-blur-sm text-[#54656f] text-[11px] font-medium px-3 py-1 rounded-lg shadow-sm">Today</span></div>
            <div className="flex justify-center mb-3"><div className="bg-[#FCE19B] text-[#8C6D1F] text-[11px] px-4 py-2.5 rounded-lg shadow-sm text-center max-w-[95%] leading-snug">🔒 Messages are end-to-end encrypted. <span className="font-bold cursor-pointer hover:underline">Learn more</span></div></div>
            {messages.map((msg) => {
              if (msg.sender === 'system') return null;
              const isUser = msg.sender === 'user';
              return (
                <div key={msg.id} className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}>
                  <div className={`relative max-w-[85%] rounded-2xl px-3 py-1.5 text-[15px] shadow-sm leading-snug ${isUser ? 'bg-[#E1F7CB] rounded-br-sm' : 'bg-white rounded-tl-sm'}`}>
                    {msg.metadata?.isPdf && (<div className="bg-black/5 rounded-xl p-2 mb-2 flex items-center gap-3 border border-black/5"><div className="bg-red-500 text-white p-2.5 rounded-lg"><FileText size={22} /></div><div className="flex-1 min-w-0 pr-2"><p className="text-[14px] font-semibold text-gray-800 truncate leading-tight">{msg.metadata.filename}</p><p className="text-[12px] text-gray-500 mt-0.5">1.2 MB • PDF</p></div></div>)}
                    <span className="text-[#111b21] whitespace-pre-wrap">{msg.text}</span>
                    <div className={`flex justify-end items-center gap-1 mt-1 -mb-0.5 ${msg.metadata?.isPdf ? 'pt-1' : ''}`}><span className="text-[11px] text-gray-500 opacity-90">{msg.timestamp}</span>{isUser && <CheckCheck size={16} className="text-[#53bdeb]" />}</div>
                  </div>
                  {msg.options && (
                    <div className="flex flex-col gap-1.5 mt-1.5 w-full max-w-[85%]">
                      {msg.options.map((opt, i) => <button key={i} onClick={opt.action} className="bg-white border border-gray-200 text-[#007AFF] shadow-sm text-[14px] font-medium py-2 px-3 rounded-xl hover:bg-gray-50 transition active:scale-95 text-center w-full">{opt.label}</button>)}
                    </div>
                  )}
                </div>
              );
            })}
            {isTyping && (<div className="flex items-start"><div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-1.5"><div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div><div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div><div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div></div></div>)}
            <div ref={chatEndRef} />
          </div>
          <div className="bg-[#f6f6f6] px-2 pt-2 pb-6 flex items-end gap-2 relative z-10 border-t border-gray-300">
            <button className="text-[#007AFF] p-1.5 flex-shrink-0 mb-1 cursor-pointer"><Plus size={28} /></button>
            <div className="flex-1 bg-white rounded-full flex items-end px-3 py-1 border border-gray-300 shadow-sm min-h-[38px] max-h-[100px] mb-1">
              <textarea value={inputText} onChange={(e) => setInputText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleUserSend(); } }} placeholder="Message" className="flex-1 bg-transparent outline-none resize-none py-[6px] text-[16px] leading-tight custom-scrollbar" rows={1} style={{minHeight: '24px'}} />
            </div>
            {inputText.trim() ? (<button onClick={() => handleUserSend()} className="bg-[#007AFF] text-white p-2 rounded-full shadow-sm hover:bg-blue-600 transition flex-shrink-0 mb-1"><Send size={18} className="ml-0.5" /></button>) : (<button className="text-[#007AFF] p-2 flex-shrink-0 mb-0.5 cursor-pointer"><Mic size={26} /></button>)}
          </div>
        </div>
      </div>
    </div>
  );

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <button onClick={onExit} className="mb-6 text-slate-500 hover:text-slate-800 transition flex items-center gap-2 font-medium">
          <ArrowLeft size={16} /> Back to Portal Hub
        </button>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-slate-100">
          <div className="text-center mb-8">
            <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"><Activity className="text-emerald-600 w-8 h-8" /></div>
            <h1 className="text-2xl font-bold text-slate-800">CuRA AI Platform</h1>
            <p className="text-slate-500 mt-2 text-sm">Secure Portal Login</p>
          </div>
          <div className="space-y-4">
            {loginError && <p className="text-red-500 text-sm font-medium">{loginError}</p>}
            <form onSubmit={handleLogin} className="space-y-4 pt-2">
              <input type="text" placeholder="Username" className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-sm" value={username} onChange={e => setUsername(e.target.value)} required />
              <input type="password" placeholder="Password" className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-sm" value={password} onChange={e => setPassword(e.target.value)} required />
              <button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 rounded-xl transition shadow-md">Authenticate</button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (userRole === 'patient') return <div className="min-h-screen bg-black flex flex-col justify-center items-center relative"><button onClick={handleLogout} className="absolute top-4 left-4 text-white/50 hover:text-white z-50 text-xs font-bold p-2 bg-white/10 rounded-lg">Logout Session</button>{renderDevicePreview()}</div>;

  return (
    <div className="flex h-screen bg-white font-sans overflow-hidden text-slate-800">
      {renderSidebar()}
      <div className="flex-1 flex flex-col relative overflow-hidden">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8 z-10 flex-shrink-0 shadow-sm">
           <div className="flex items-center gap-3 md:hidden"><Menu className="text-slate-500" /><h1 className="font-bold text-lg text-slate-800">CuRA AI</h1></div>
           <div className="hidden md:block"><h2 className="font-bold text-slate-800 capitalize tracking-wide">{activeTab.replace('-', ' ')}</h2></div>
           <div className="flex items-center gap-4">{!isDeviceOpen && (<button onClick={() => setIsDeviceOpen(true)} className="flex items-center gap-2 text-sm font-semibold bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg border border-emerald-200 hover:bg-emerald-100 transition">📱 Show Device Preview</button>)}</div>
        </header>
        <div className="flex-1 overflow-y-auto bg-slate-50/50 relative">
          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'inbox' && renderSharedInbox()}
          {activeTab === 'billing' && renderBilling()}
          {activeTab === 'settings' && renderSettings()}
          {activeTab === 'patients' && renderDirectory()}
        </div>
      </div>
      {renderDevicePreview()}
      {renderModals()}
      {toastMsg && (<div className="fixed bottom-6 right-1/2 transform translate-x-1/2 md:translate-x-0 md:right-auto md:left-6 bg-slate-800 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 z-[110]"><CheckCircle2 size={18} className="text-emerald-400" /><span className="text-sm font-medium">{toastMsg}</span></div>)}
    </div>
  );
};


// ============================================================================
// 6. MAIN PORTAL HUB (LANDING PAGE) & APP ENTRY
// ============================================================================

export default function App() {
  const [currentApp, setCurrentApp] = useState('landing'); // 'landing', 'brochure', 'demo'

  const LandingPage = () => (
    <div className="min-h-screen bg-slate-900 relative overflow-x-hidden flex items-center justify-center p-6 selection:bg-teal-500/30 font-sans">
      
      {/* Liquid Glass Background - Unified aesthetic */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-teal-600/30 blur-[120px] mix-blend-screen"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[60vw] h-[60vw] rounded-full bg-blue-800/40 blur-[150px] mix-blend-screen"></div>
        <div className="absolute top-[40%] left-[60%] w-[30vw] h-[30vw] rounded-full bg-purple-600/20 blur-[100px] mix-blend-screen"></div>
      </div>

      <div className="relative z-10 max-w-7xl w-full flex flex-col lg:flex-row items-center gap-12 xl:gap-16 py-12">
        
        {/* Left Section: Branding & Welcome */}
        <div className="lg:w-1/3 text-center lg:text-left space-y-8 animate-in fade-in slide-in-from-left-8 duration-700">
          <img 
            src="https://canvasfuture.com/wp-content/uploads/2025/10/LOGO-_Main.png" 
            alt="Canvas Future Logo" 
            className="h-[180px] md:h-[220px] lg:h-[290px] object-contain mx-auto lg:mx-0 filter drop-shadow-2xl" 
          />
          
          <div className="space-y-4">
            <h1 className="text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-teal-100 leading-tight">
              Welcome to CuRA AI
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 font-light leading-relaxed max-w-2xl mx-auto lg:mx-0">
              The Intelligent WhatsApp Automation System built exclusively for Modern IVF Clinics.
            </p>
          </div>
          
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-slate-300 text-sm font-medium">
            <ShieldCheck size={16} className="text-teal-400" /> Medical-Grade AI Integration
          </div>
        </div>

        {/* Right Section: Navigation Cards */}
        <div className="lg:w-2/3 grid grid-cols-1 md:grid-cols-3 gap-6 w-full animate-in fade-in slide-in-from-right-8 duration-700">
          
          {/* Card 1: Brochure */}
          <div 
            onClick={() => setCurrentApp('brochure')}
            className="bg-white/10 backdrop-blur-xl border border-white/20 shadow-[0_8px_32px_0_rgba(31,38,135,0.37)] rounded-3xl p-8 flex flex-col items-center text-center hover:bg-white/15 transition-all duration-300 group cursor-pointer hover:scale-[1.02]"
          >
            <div className="w-24 h-24 mb-6 bg-gradient-to-br from-teal-500/20 to-emerald-500/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500 border border-teal-500/30 relative overflow-hidden">
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-30"></div>
              <FileText size={40} className="text-teal-400 relative z-10" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Platform Brochure</h3>
            <p className="text-slate-400 text-sm mb-8 leading-relaxed flex-1">
              Explore the full capabilities, ROI projections, architectural pillars, and pricing tiers.
            </p>
            <button className="w-full py-3 bg-teal-600/90 hover:bg-teal-500 text-white rounded-xl font-bold transition-colors flex justify-center items-center gap-2 shadow-lg shadow-teal-900/50 border border-teal-400/50">
              View Brochure <ChevronRight size={18} />
            </button>
          </div>

          {/* Card 2: Live Demo */}
          <div 
            onClick={() => setCurrentApp('demo')}
            className="bg-white/10 backdrop-blur-xl border border-white/20 shadow-[0_8px_32px_0_rgba(31,38,135,0.37)] rounded-3xl p-8 flex flex-col items-center text-center hover:bg-white/15 transition-all duration-300 group cursor-pointer hover:scale-[1.02]"
          >
            <div className="w-24 h-24 mb-6 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500 border border-blue-500/30 relative overflow-hidden">
               <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-30"></div>
              <Activity size={40} className="text-blue-400 relative z-10" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Live Automation Demo</h3>
            <p className="text-slate-400 text-sm mb-8 leading-relaxed flex-1">
              Access the interactive clinic command center, shared inbox, and patient device preview.
            </p>
            <button className="w-full py-3 bg-blue-600/90 hover:bg-blue-500 text-white rounded-xl font-bold transition-colors flex justify-center items-center gap-2 shadow-lg shadow-blue-900/50 border border-blue-400/50">
              Launch Demo <ChevronRight size={18} />
            </button>
          </div>

          {/* Card 3: Book a Demo */}
          <div 
            onClick={() => window.open('https://outlook.office.com/book/CuraAIDemo@lifetwin.in/', '_blank')}
            className="bg-white/10 backdrop-blur-xl border border-white/20 shadow-[0_8px_32px_0_rgba(31,38,135,0.37)] rounded-3xl p-8 flex flex-col items-center text-center hover:bg-white/15 transition-all duration-300 group cursor-pointer hover:scale-[1.02]"
          >
            <div className="w-24 h-24 mb-6 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500 border border-purple-500/30 relative overflow-hidden">
               <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-30"></div>
              <CalendarClock size={40} className="text-purple-400 relative z-10" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Book a Demo</h3>
            <p className="text-slate-400 text-sm mb-8 leading-relaxed flex-1">
              Schedule a personalized 1-on-1 walkthrough of the CuRA AI platform with our integration experts.
            </p>
            <button className="w-full py-3 bg-purple-600/90 hover:bg-purple-500 text-white rounded-xl font-bold transition-colors flex justify-center items-center gap-2 shadow-lg shadow-purple-900/50 border border-purple-400/50">
              Schedule Now <ChevronRight size={18} />
            </button>
          </div>

        </div>
      </div>
    </div>
  );

  return (
    <>
      {currentApp === 'landing' && <LandingPage />}
      {currentApp === 'brochure' && <BrochureApp onExit={() => setCurrentApp('landing')} />}
      {currentApp === 'demo' && <DemoWorkbench onExit={() => setCurrentApp('landing')} />}
    </>
  );
}